/* eslint-disable react/prop-types */
"use client";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { useProfileCardModal } from "../../context/ProfileCardContext";
import { EventChatMobileView } from "../../components/menu/ChatMobileView";
import { EventChatDesktopView } from "../../components/menu/ChatDesktopView";
import ChatInputBar from "../../components/menu/ChatInputBar";
import WelcomeCrewPanel from "../../components/menu/WelcomeCrewPanel";
import ChatMessageAttentionModal from "../../components/menu/ChatMessageAttentionModal";
import ChatParticipantReportModal from "../../components/menu/ChatParticipantReportModal";
import ClubChatNotificationsModal from "../../components/menu/ClubChatNotificationsModal";
import { ChatPollSheet, ChatLocationSheet } from "../../components/menu/ChatAttachmentSheets";
import ChatLocationMapModal from "../../components/menu/ChatLocationMapModal";
import RemoveParticipantModal from "../../components/events/RemoveParticipantModal";
import ConceptDatePollModal from "../../components/events/ConceptDatePollModal";
import useChatAttachments from "../../components/menu/hooks/useChatAttachments";
import {
  CHAT_MAX_FILE_MB,
  CHAT_MAX_FILE_BYTES,
} from "../../constants/chatUploads";
import { normalizeMessageReactions } from "../../utils/chatReactions";
import { applyChatPinnedMessages, getChatPinnedMessages, removeChatPinnedMessage } from "../../utils/chatPinnedMessages";
import {
  buildChatLocationMarkers,
  isChatPollPayload,
} from "../../utils/chatStructuredPayloads";
import { buildWsUrl, toApiAbsoluteUrl } from "../../utils/ws";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { resolveChatOpenReadTarget } from "../../utils/chatReadState";
import {
  reconcileChatReadPosition,
  rememberChatRead,
} from "../../utils/chatReadPosition";
import {
  DELETED_USER_LABEL,
  formatUserHandle,
  isDeletedAccountUser,
  isDeletedUsername,
} from "../../utils/deletedAccount";
import useChatZoomLock from "../../hooks/useChatZoomLock";
import useChatDraftPersistence from "../../hooks/useChatDraftPersistence";
import { buildChatDraftStorageKey, loadChatDraft } from "../../utils/chatDrafts";
import useEventAttendanceWindow from "../../hooks/useEventAttendanceWindow";
import useChatVisualViewportStyle from "../../hooks/useDirectChatVisualViewport";
import { buildEventPath } from "../../utils/eventUrl";
import { buildClubPath } from "../../utils/clubUrl";
import { RequiredActionsContext } from "../../context/RequiredActionsContext";
import {
  captureChatPrependPosition,
  findOldestChatItemId,
  prependOlderChatPage,
  restoreChatPrependPosition,
} from "../../utils/chatPrependScroll";
import { resolveFirstUnreadMessageId } from "../../utils/chatUnreadFocus";
import { scheduleChatFeedScrollToBottom } from "../../utils/chatAutoScroll";
import { loadChatMessagePage } from "../../utils/chatMessageRequest";
import { stabilizeChatInitialPosition } from "../../utils/chatInitialPosition";
import {
  buildOptimisticChatMessage,
  findMatchingOptimisticClientId,
  reconcileOptimisticChatMessage,
  removeOptimisticChatMessage,
} from "../../utils/chatOptimisticMessages";
import { isInternalToolingAvailable } from "../../config/mobileRuntime";
import {
  isInboxEventForOpenChat,
  mergeLatestChatPage,
} from "../../utils/chatRealtimeSync";
import { getChatSticker } from "../../constants/chatStickers";
const ITEM_TYPES = {
  MESSAGE: "message",
  SYSTEM: "system",
  SYSTEM_AS_USER: "system_as_user",
  DAY: "day_separator",
};

const PAGE_LIMIT = 50;
const DEFAULT_MESSAGE_ATTENTION_LIMIT = 2;

// System-as-user (welcome) message rendering
const SYSTEM_USER_CODE = "VG";
const SYSTEM_USER_USERNAME = "@vaultgrid";
const SYSTEM_USER_ROLE = "system";
// Put the logo/avatar here: public/assets/avatars/vg-system.png
const SYSTEM_USER_AVATAR_SRC = "/assets/avatars/vaultgrid.png";
const SUPPORT_USER_CODE = "SUP";
const SUPPORT_USER_AVATAR_SRC = "/assets/menu/support_chat.png";
const CHAT_UPLOAD_TARGET_BYTES = 500 * 1024;
const CHAT_UPLOAD_ATTEMPTS = [
  { maxSide: 1600, quality: 0.82 },
  { maxSide: 1280, quality: 0.76 },
  { maxSide: 1024, quality: 0.68 },
  { maxSide: 800, quality: 0.58 },
];
const TECHNICAL_EVENT_TITLES = new Set(["event chat", "chat"]);

const SYSTEM_MESSAGE_I18N_KEYS = {
  "chats.system.joined": "chats.event_chat.system_messages.joined",
  "chats.system.joined_as_host": "chats.event_chat.system_messages.joined_as_host",
  "chats.system.left": "chats.event_chat.system_messages.left",
  "chats.system.left_due_to_time_mismatch": "chats.event_chat.system_messages.left_due_to_time_mismatch",
  "chats.system.left_due_to_plans_changed": "chats.event_chat.system_messages.left_due_to_plans_changed",
  "chats.system.left_due_to_intent_timeout": "chats.event_chat.system_messages.left_due_to_intent_timeout",
  "chats.system.left_due_to_activation_date_timeout": "chats.event_chat.system_messages.left_due_to_activation_date_timeout",
  "chats.system.left_due_to_waitlist_slot_timeout": "chats.event_chat.system_messages.left_due_to_waitlist_slot_timeout",
  "chats.system.left_due_to_waitlist_slot_decline": "chats.event_chat.system_messages.left_due_to_waitlist_slot_decline",
  "chats.system.kicked": "chats.event_chat.system_messages.kicked",
};

function getViewerLocalizedSystemText(message, t) {
  const fallback = String(message?.text || "");
  const payload = message?.payload && typeof message.payload === "object" ? message.payload : {};
  if (payload.system_kind === "new_joiners_member_joined") return fallback;
  const frontendKey = SYSTEM_MESSAGE_I18N_KEYS[String(payload.system_i18n_key || "").trim()];
  if (!frontendKey) return fallback;
  const params =
    payload.system_i18n_params && typeof payload.system_i18n_params === "object"
      ? payload.system_i18n_params
      : {};
  return t(frontendKey, { ...params, defaultValue: fallback });
}

function normalizeDisplayTitle(value) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (TECHNICAL_EVENT_TITLES.has(text.toLowerCase())) return "";
  return text;
}

function resolveEventDisplayTitle(chat, eventForCard) {
  const candidates = [
    eventForCard?.name,
    eventForCard?.title,
    eventForCard?.event_name,
    eventForCard?.event_title,
    chat?.event_name,
    chat?.event_title,
    chat?.event?.name,
    chat?.event?.title,
    chat?.title,
    chat?.name,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeDisplayTitle(candidate);
    if (normalized) return normalized;
  }

  return "";
}

function extractEventIdFromPath(value) {
  const text = String(value || "").trim();
  if (!text) return null;

  const match = text.match(/\/events\/(\d+)(?:[/-]|$|\?)/i) || text.match(/^(\d+)(?:[/-]|$|\?)/);
  if (!match) return null;

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getParticipantUserId(participant) {
  const parsed = Number(participant?.user_id || participant?.user?.id || participant?.id || 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function normalizeParticipantCode(value) {
  return String(value || "").trim().toUpperCase();
}

function getParticipantSelectionId(participant) {
  const userId = getParticipantUserId(participant);
  if (userId) return `id:${userId}`;
  const code = normalizeParticipantCode(participant?.code || participant?.user?.code);
  return code ? `code:${code}` : "";
}


function formatShortTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  } catch {
    return "";
  }
}

function normalizeWsAttachments(rawAtt) {
  const list = Array.isArray(rawAtt) ? rawAtt : [];
  return list
    .map((a) => {
      const type = a?.type || a?.attachment_type || null;
      const rawUrl = a?.url || a?.file_url || a?.file || a?.external_url || null;
      const url = rawUrl ? toApiAbsoluteUrl(rawUrl) : null;
      if (!type || !url) return null;
      return {
        type,
        url,
        width: a?.width ?? null,
        height: a?.height ?? null,
        original_name: a?.original_name ?? null,
        size_bytes: a?.size_bytes ?? null,
      };
    })
    .filter(Boolean);
}

function buildAvatarSrc(userMeta) {
  return resolveUserAvatarSrc(userMeta);
}

function extractAvatarFromMessage(message) {
  if (!message) return null;

  const userMeta = {
    avatar_custom_url:
      message.author_avatar_custom_url ||
      message.author?.avatar_custom_url ||
      null,
    avatar_url:
      message.author_avatar_url ||
      message.author?.avatar_url ||
      null,
    avatar:
      message.author_avatar ||
      message.author?.avatar ||
      null,
  };

  return buildAvatarSrc(userMeta);
}

function _looksLikeHeic(file) {
  const mime = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  return (
    mime.includes("heic") ||
    mime.includes("heif") ||
    name.endsWith(".heic") ||
    name.endsWith(".heif")
  );
}

function _isSmallJpeg(file) {
  const mime = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  const isJpeg =
    mime === "image/jpeg" ||
    mime === "image/jpg" ||
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg");
  return isJpeg && Number(file?.size || 0) > 0 && Number(file?.size || 0) <= CHAT_UPLOAD_TARGET_BYTES;
}

function _chatUploadFileName(file) {
  const baseName = String(file?.name || "chat-image").replace(/\.[^/.]+$/, "") || "chat-image";
  return `${baseName}.jpg`;
}

function _canvasToBlob(canvas, type, quality) {
  if (typeof canvas.toBlob === "function") {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("jpeg_encode_failed"));
        },
        type,
        quality
      );
    });
  }

  const dataUrl = canvas.toDataURL(type, quality);
  const parts = dataUrl.split(",");
  const byteString = window.atob(parts[1] || "");
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i += 1) {
    bytes[i] = byteString.charCodeAt(i);
  }
  return Promise.resolve(new Blob([bytes], { type }));
}

async function _loadImageElement(blob) {
  const objectUrl = URL.createObjectURL(blob);
  try {
    return await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("image_decode_failed"));
      el.src = objectUrl;
    });
  } finally {
    try {
      URL.revokeObjectURL(objectUrl);
    } catch {
      // ignore
    }
  }
}

async function _compressImageBlobToJpeg(blob, fileName) {
  const img = await _loadImageElement(blob);
  const sourceWidth = Math.max(1, Number(img.naturalWidth || img.width || 0));
  const sourceHeight = Math.max(1, Number(img.naturalHeight || img.height || 0));
  let lastBlob = null;

  for (const attempt of CHAT_UPLOAD_ATTEMPTS) {
    const scale = Math.min(attempt.maxSide / Math.max(sourceWidth, sourceHeight), 1);
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("canvas_not_supported");
    }

    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    lastBlob = await _canvasToBlob(canvas, "image/jpeg", attempt.quality);
    if (Number(lastBlob?.size || 0) <= CHAT_UPLOAD_TARGET_BYTES) {
      break;
    }
  }

  return new File([lastBlob], fileName, { type: "image/jpeg" });
}

async function _convertHeicToJpegBlob(file) {
  const mod = await import("heic2any");
  const heic2any = mod.default || mod;
  const result = await heic2any({
    blob: file,
    toType: "image/jpeg",
    quality: 0.82,
  });
  return Array.isArray(result) ? result[0] : result;
}

async function _prepareChatImageForUpload(file) {
  if (!file) return null;
  if (_isSmallJpeg(file)) return file;

  const fileName = _chatUploadFileName(file);
  try {
    return await _compressImageBlobToJpeg(file, fileName);
  } catch (firstError) {
    if (!_looksLikeHeic(file)) {
      firstError.code = "IMAGE_PREP_FAILED";
      throw firstError;
    }

    try {
      const jpegBlob = await _convertHeicToJpegBlob(file);
      return await _compressImageBlobToJpeg(jpegBlob, fileName);
    } catch (heicError) {
      heicError.code = "IMAGE_PREP_FAILED";
      throw heicError;
    }
  }
}

export default function Chat({
  chat,
  onBack,
  onInitialReady = null,
  onInitialError = null,
  deferInputUntilReady = false,
}) {
  const { t, i18n } = useTranslation("menu");
  const { t: te } = useTranslation("events");
  const [hostRulesReminderSending, setHostRulesReminderSending] = useState(false);
  const [commercialNoticeSending, setCommercialNoticeSending] = useState(false);
  const [moderationResolutionSending, setModerationResolutionSending] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const focusFirstUnreadFromPush = location?.state?.focusFirstUnread === true;
  const pushUnreadFocusKey = focusFirstUnreadFromPush
    ? [
        location?.key || "default",
        location?.state?.pushNotificationId || "",
        location?.state?.pushMessageId || "",
      ].join(":")
    : "";
  const {
    actions: requiredActions,
    dismissForSession: dismissRequiredActionForSession,
  } = React.useContext(RequiredActionsContext);
  useChatZoomLock(true);

  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return window.matchMedia("(min-width: 768px)").matches;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    let mq;
    try {
      mq = window.matchMedia("(min-width: 768px)");
    } catch {
      return;
    }

    const handler = () => setIsDesktop(!!mq.matches);
    handler();

    try {
      if (typeof mq.addEventListener === "function") mq.addEventListener("change", handler);
      else if (typeof mq.addListener === "function") mq.addListener(handler);
    } catch {
      // ignore
    }

    return () => {
      try {
        if (typeof mq.removeEventListener === "function") mq.removeEventListener("change", handler);
        else if (typeof mq.removeListener === "function") mq.removeListener(handler);
      } catch {
        // ignore
      }
    };
  }, []);

  const rawChatKind = String(chat?.kind || "").trim().toLowerCase();
  const chatKind = rawChatKind === "support" ? "support" : rawChatKind === "group" ? "group" : "event";
  const isSupportChat = chatKind === "support";
  const [sendWithPersonalPhoto, setSendWithPersonalPhoto] = useState(false);
  const eventId = chatKind === "event" ? (chat?.event_id || chat?.event?.id || chat?.id) : null;
  const supportChatId = isSupportChat ? (chat?.chat_id || chat?.id || null) : null;
  useEffect(() => {
    setSendWithPersonalPhoto(false);
  }, [supportChatId]);
  const internalChatId =
    isSupportChat
      ? supportChatId
      : chatKind === "group"
      ? (chat?.chat_id || chat?.id || null)
      : eventId;
  const notificationChatId =
    chat?.chat_id || (chatKind === "event" ? null : internalChatId);
  const participantsEndpoint =
    isSupportChat
      ? null
      : chatKind === "group"
      ? (internalChatId ? `/chats/groups/${internalChatId}/participants/` : null)
      : (eventId ? `/chats/${eventId}/internal-chat/participants/` : null);
  const messagesBaseUrl =
    isSupportChat
      ? (internalChatId ? `/chats/support/${internalChatId}` : null)
      : chatKind === "group"
      ? (internalChatId ? `/chats/groups/${internalChatId}` : null)
      : (eventId ? `/chats/${eventId}/internal-chat` : null);
  const wsPath =
    isSupportChat
      ? (internalChatId ? `/ws/chats/support/${internalChatId}/` : null)
      : chatKind === "group"
      ? (internalChatId ? `/ws/chats/group/${internalChatId}/` : null)
      : (eventId ? `/ws/chats/event/${eventId}/` : null);
  const modalTabKey = isSupportChat ? "all" : chatKind === "group" ? "groups" : "events";

  // ---- Mock fallbacks (until automation) ----
  const dateTime = chat?.date_time || null;
  const [participantsCount, setParticipantsCount] = useState(
    Number(chat?.participants_count ?? 0)
  );
  const [participantsReloadKey, setParticipantsReloadKey] = useState(0);
  const [chatParticipantsSnapshot, setChatParticipantsSnapshot] = useState([]);
  const [chatParticipantsCanRemove, setChatParticipantsCanRemove] = useState(false);
  const [removeParticipantModalOpen, setRemoveParticipantModalOpen] = useState(false);
  const [removalParticipants, setRemovalParticipants] = useState([]);
  const [removalParticipantsCanRemove, setRemovalParticipantsCanRemove] = useState(false);
  const [removalParticipantsLoading, setRemovalParticipantsLoading] = useState(false);
  const isFavourite = !!chat?.is_favourite;

  const eventForCard = chatKind === "event" ? (chat?.event || chat || {}) : {};

  const supportDisplayTitle =
    String(chat?.display_name || chat?.title || chat?.name || "").trim() ||
    t("chats.support.title", { defaultValue: "Support" });
  const eventName = isSupportChat ? supportDisplayTitle : resolveEventDisplayTitle(chat, eventForCard);
  const eventDateTime = eventForCard.date_time || dateTime;
  const eventCity = eventForCard.city || "";
  const eventStreet = eventForCard.street || "";
  const eventBuildingNumber = eventForCard.building_number || "";
  const eventStatus = eventForCard.status;
  const normalizedEventStatus = String(eventStatus || "").trim().toLowerCase();
  const eventIsVerified = eventForCard.is_verified;
  const eventIsValidated = eventForCard.is_validated;
  const eventType = eventForCard.type || "event";
  const normalizedEventType = String(eventType || "").trim().toLowerCase();
  const isConceptEvent = normalizedEventType === "concept";
  const [isViewerAttendanceConfirmed, setIsViewerAttendanceConfirmed] = useState(false);
  const [participantPrimaryAction, setParticipantPrimaryAction] = useState({
    isParticipant: false,
    isHost: false,
    action: null,
    attended: false,
  });
  const initialConceptActivationMaxParticipants = Number(
    eventForCard?.max_participants ?? chat?.max_participants ?? 0
  );
  const [conceptActivationMaxParticipants, setConceptActivationMaxParticipants] = useState(
    Number.isFinite(initialConceptActivationMaxParticipants) && initialConceptActivationMaxParticipants > 0
      ? initialConceptActivationMaxParticipants
      : 0
  );
  const [conceptActivationLaunching, setConceptActivationLaunching] = useState(false);
  const [isLocalAttendanceCtaDebug] = useState(
    () =>
      isInternalToolingAvailable() &&
      String(process.env.REACT_APP_CHAT_ATTENDANCE_ALWAYS_VISIBLE || "").trim() === "1"
  );
  const attendanceWindowOpenedByTime = useEventAttendanceWindow(eventDateTime);


  const [items, setItems] = useState([]);
  useEffect(() => {
    setItems((current) =>
      current.map((item) => {
        if (item?.type !== ITEM_TYPES.SYSTEM && item?.message_type !== "system_as_user") {
          return item;
        }
        const localizedText = getViewerLocalizedSystemText(item, t);
        if (!localizedText || localizedText === item.text) return item;
        return {
          ...item,
          text: localizedText,
          ...(item?.type === ITEM_TYPES.SYSTEM ? { lines: [localizedText] } : {}),
        };
      })
    );
  }, [i18n.resolvedLanguage, t]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserIsSuperuser, setCurrentUserIsSuperuser] = useState(false);
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;
  const [typingUsers, setTypingUsers] = useState([]);
  const typingTimersRef = useRef({});
  const chatWsRef = useRef(null);
  const wsReconnectTimerRef = useRef(null);
  const wsReconnectAttemptRef = useRef(0);
  const latestSyncTimerRef = useRef(null);
  const [latestSyncVersion, setLatestSyncVersion] = useState(0);
  const typingStateRef = useRef(false);
  const typingStopTimerRef = useRef(null);
  const typingLastSentRef = useRef(0);
  const buddyMeetTokenRef = useRef("");
  const requestLatestSync = useCallback(() => {
    if (latestSyncTimerRef.current) return;
    latestSyncTimerRef.current = window.setTimeout(() => {
      latestSyncTimerRef.current = null;
      setLatestSyncVersion((version) => version + 1);
    }, 0);
  }, []);

  useEffect(() => () => {
    if (latestSyncTimerRef.current) {
      window.clearTimeout(latestSyncTimerRef.current);
      latestSyncTimerRef.current = null;
    }
  }, []);

  // ===== Unread/read meta (MVP) =====
  const [chatMeta, setChatMeta] = useState({
    last_read_message_id: 0,
    unread_count: 0,
    first_unread_message_id: 0,
    last_message_id: 0,
    can_moderate_messages: false,
    can_send_messages: true,
    send_disabled_reason: "",
    can_request_message_attention: false,
    message_attention_requests_used: 0,
    message_attention_requests_limit: DEFAULT_MESSAGE_ATTENTION_LIMIT,
    assistant_state: chat?.assistant_state || null,
    staff_moderation_assessment: null,
    club: chat?.club || null,
    new_joiners_event_cta: chat?.new_joiners_event_cta || null,
    buddy_state: chat?.buddy_state || null,
    notifications_muted: Boolean(chat?.notifications_muted),
  });
  const clubChat = chatMeta?.club || chat?.club || null;
  const groupDisplayTitle =
    chatKind === "group"
      ? (
          clubChat?.name ||
          (
            chat?.cohort?.group_kind === "new_joiners"
              ? (
                  String(chat?.cohort?.title || chat?.name || "").trim() ||
                  t("chats.groups.new_joiners_title")
                )
              : String(chat?.cohort?.title || chat?.name || "Group").replace(/\s+test$/i, "").trim()
          )
        ) || "Group"
      : "";
  const headerTitle =
    isSupportChat
      ? supportDisplayTitle
      : chatKind === "group"
      ? groupDisplayTitle
      : eventName;
  const [isGroupBotToggleLoading, setIsGroupBotToggleLoading] = useState(false);
    // Pagination for older history
  const [hasOlder, setHasOlder] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const pendingJumpTargetRef = useRef(null);
  const pendingJumpAttemptsRef = useRef(0);
  const pendingJumpShouldFlashRef = useRef(false);
  const loadOlderMessagesRef = useRef(null);

  // One-time initial positioning when opening the chat
  const didInitialScrollRef = useRef(false);
  const initialPositionCleanupRef = useRef(null);
  const initialReadyNotifiedRef = useRef(false);
  const [initialMessagesSettled, setInitialMessagesSettled] = useState(
    () => !messagesBaseUrl
  );
  const [initialParticipantsSettled, setInitialParticipantsSettled] = useState(
    () => !participantsEndpoint
  );
  const [initialParticipantActionSettled, setInitialParticipantActionSettled] = useState(
    () => chatKind !== "event" || !eventId
  );
  const [initialPositionSettled, setInitialPositionSettled] = useState(
    () => !messagesBaseUrl
  );

  // Throttle/RAF helpers for scroll-based read marking
  const rafScrollRef = useRef(null);
  const readUpToTimerRef = useRef(null);
  const lastSentReadUpToRef = useRef(0);

  // Keep latest values for unmount cleanup (leaving chat not via Back button)
  const lastMessageIdRef = useRef(0);
  const messagesBaseUrlRef = useRef(null);
  const latestLoadedBaseUrlRef = useRef(null);

  useEffect(() => {
    if (readUpToTimerRef.current) {
      window.clearTimeout(readUpToTimerRef.current);
      readUpToTimerRef.current = null;
    }
    lastSentReadUpToRef.current = 0;
    lastMessageIdRef.current = 0;
  }, [messagesBaseUrl]);

  // Used to allow auto-scroll to bottom after I send a message
  const justSentRef = useRef(false);
  const sendInFlightRef = useRef(false);
  // Guard: true while we are prepending older messages (to avoid padding/layout recomputes that cause scroll jumps)
  const isPrependingRef = useRef(false);

  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const [pollSheetOpen, setPollSheetOpen] = useState(false);
  const [locationSheetOpen, setLocationSheetOpen] = useState(false);
  const [conceptDatePollModalOpen, setConceptDatePollModalOpen] = useState(false);
  const [locationMapMessageId, setLocationMapMessageId] = useState(null);

  // ===== Attachments (images only) MVP =====
  const {
    fileInputRef,
    pendingImages,
    onPickImages,
    openNativeImagePicker,
    removePendingImage,
    clearPendingImages,
    MAX_ATTACHMENTS,
  } = useChatAttachments({ allowAttachments: true });

  const [avatarByCode, setAvatarByCode] = useState({
    [SYSTEM_USER_CODE]: SYSTEM_USER_AVATAR_SRC,
    [SUPPORT_USER_CODE]: SUPPORT_USER_AVATAR_SRC,
  });
  const [myRoleDetected, setMyRoleDetected] = useState(null);
  const locationMarkers = React.useMemo(() => buildChatLocationMarkers(items), [items]);

  const isHostRole = (role) => {
    const r = (role || "").toString().toLowerCase();
    return r === "host" || r === "admin" || r === "owner" || r === "creator";
  };

  const openEventFromChatHeader = () => {
    const buddyEvent = chatMeta?.buddy_state?.kind === "welcome_crew"
      ? chatMeta.buddy_state.event
      : null;
    const targetEventId = Number(eventId || buddyEvent?.id || 0);
    if (!targetEventId || (chatKind !== "event" && !buddyEvent)) return;
    const targetEventName = buddyEvent?.name || eventName || chat?.event?.name || chat?.name || "";

    navigate(buildEventPath(targetEventId, targetEventName), {
      state: {
        fromEventChat: true,
        returnToChatPath: window.location.pathname,
      },
    });
  };

  const openConfirmAttendanceFromChat = () => {
    if (chatKind !== "event" || !eventId) return;
    const basePath = buildEventPath(eventId, eventName || chat?.event?.name || chat?.name || "");
    navigate(`${basePath}?confirm=1`);
  };

  const openConceptActivationFromChatHeader = React.useCallback(async () => {
    if (chatKind !== "event" || !eventId || conceptActivationLaunching) return;
    setConceptActivationLaunching(true);
    try {
      const [eventRes, hostRatingRes, pollRes] = await Promise.all([
        api.get(`/events/details/${eventId}/`),
        api.get(`/events/get-host-rating/`).catch(() => ({ data: null })),
        api.get(`/events/${eventId}/concept-date-poll/`).catch(() => ({ data: null })),
      ]);

      dismissRequiredActionForSession?.();
      navigate(`/events/edit?activate_concept=${eventId}`, {
        state: {
          mode: "activate_concept",
          eventData: eventRes?.data || { id: eventId, type: "concept" },
          conceptDatePoll: pollRes?.data?.poll || null,
          activationMode: true,
          hostRating: hostRatingRes?.data || null,
        },
      });
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        te("events.details.failed_to_load", { defaultValue: "Failed to load event details." });
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message, status: err?.response?.status },
        })
      );
    } finally {
      setConceptActivationLaunching(false);
    }
  }, [
    chatKind,
    conceptActivationLaunching,
    dismissRequiredActionForSession,
    eventId,
    navigate,
    te,
  ]);

  const openClubFromChatHeader = () => {
    const nextClub = chatMeta?.club || chat?.club || null;
    if (!nextClub?.id) return;
    navigate(buildClubPath(nextClub.id, nextClub.name || ""));
  };

  const openSubscriptionFromClubChat = React.useCallback(() => {
    const nextClub = chatMeta?.club || chat?.club || null;
    const target = String(nextClub?.subscription_path || "/menu/subscription").trim();
    if (!target) return;
    navigate(target);
  }, [chat, chatMeta, navigate]);

  const searchParams = React.useMemo(
    () => new URLSearchParams(location?.search || ""),
    [location?.search]
  );
  const requestedMessageId = React.useMemo(() => {
    const rawValue = String(searchParams.get("message_id") || "").trim();
    if (!/^\d+$/.test(rawValue)) return null;
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);

  // Reset initial-position guard on chat switch/reopen
  useEffect(() => {
    initialPositionCleanupRef.current?.();
    initialPositionCleanupRef.current = null;
    didInitialScrollRef.current = false;
    initialReadyNotifiedRef.current = false;
    setInitialMessagesSettled(!messagesBaseUrl);
    setInitialParticipantsSettled(!participantsEndpoint);
    setInitialParticipantActionSettled(chatKind !== "event" || !eventId);
    setInitialPositionSettled(!messagesBaseUrl);
  }, [
    chatKind,
    eventId,
    messagesBaseUrl,
    participantsEndpoint,
    pushUnreadFocusKey,
    requestedMessageId,
  ]);

  useEffect(
    () => () => {
      initialPositionCleanupRef.current?.();
      initialPositionCleanupRef.current = null;
    },
    []
  );

  useEffect(() => {
    if (
      initialReadyNotifiedRef.current ||
      !initialMessagesSettled ||
      !initialParticipantsSettled ||
      !initialParticipantActionSettled ||
      !initialPositionSettled
    ) {
      return;
    }
    initialReadyNotifiedRef.current = true;
    onInitialReady?.();
  }, [
    initialMessagesSettled,
    initialParticipantActionSettled,
    initialParticipantsSettled,
    initialPositionSettled,
    onInitialReady,
  ]);

  const { openProfileCard } = useProfileCardModal();
  const openProfileByCode = openProfileCard;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get("/accounts/profile/");
        if (cancelled) return;
        const uid = Number(resp?.data?.id || 0);
        setCurrentUserId(Number.isFinite(uid) && uid > 0 ? uid : null);
        setCurrentUserIsSuperuser(Boolean(resp?.data?.is_superuser));
      } catch {
        if (!cancelled) {
          setCurrentUserId(null);
          setCurrentUserIsSuperuser(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!participantsEndpoint) {
      setInitialParticipantsSettled(true);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const resp = await api.get(participantsEndpoint, { timeout: 12000 });
        if (cancelled) return;

        const list = resp?.data?.participants || [];
        const resolvedCount = Number(resp?.data?.participants_count);
        const map = {};
        for (const u of list) {
          const code = (u?.code || "").toString();
          const src = buildAvatarSrc(u);
          if (code && src) map[code] = src;
        }

        setParticipantsCount(
          Number.isFinite(resolvedCount) && resolvedCount >= 0 ? resolvedCount : list.length
        );
        setChatParticipantsSnapshot(list);

        // Detect my role from participants payload (preferred, because chat object may not contain my_role)
        const me =
          list.find((u) => u?.is_self === true) ||
          list.find((u) => u?.is_me === true) ||
          list.find((u) => u?.me === true) ||
          null;

        const detectedRole =
          me?.role ||
          me?.member_role ||
          me?.author_role ||
          me?.event_role ||
          me?.chat_role ||
          me?.my_role ||
          null;

        const nextCanRemove = isHostRole(detectedRole);
        setChatParticipantsCanRemove(nextCanRemove);
        if (chatKind === "event") {
          setRemovalParticipants(list);
          setRemovalParticipantsCanRemove(nextCanRemove);
        }

        const meId = Number(me?.id || me?.user_id || 0);
        if (Number.isFinite(meId) && meId > 0) {
          setCurrentUserId(meId);
        }
        setIsViewerAttendanceConfirmed(Boolean(me?.attended));

        setMyRoleDetected(detectedRole);
        setAvatarByCode((prev) => ({
          ...prev,
          ...map,
          [SYSTEM_USER_CODE]: SYSTEM_USER_AVATAR_SRC,
          [SUPPORT_USER_CODE]: SUPPORT_USER_AVATAR_SRC,
        }));
      } catch (e) {
        if (!cancelled) {
          setIsViewerAttendanceConfirmed(false);
          setAvatarByCode({
            [SYSTEM_USER_CODE]: SYSTEM_USER_AVATAR_SRC,
            [SUPPORT_USER_CODE]: SUPPORT_USER_AVATAR_SRC,
          });
          setChatParticipantsSnapshot([]);
          setChatParticipantsCanRemove(false);
          if (chatKind === "event") {
            setRemovalParticipants([]);
            setRemovalParticipantsCanRemove(false);
          }
        }
      } finally {
        if (!cancelled) setInitialParticipantsSettled(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chatKind, participantsEndpoint]);

  useEffect(() => {
    if (chatKind !== "event" || !eventId) {
      setParticipantPrimaryAction({
        isParticipant: false,
        isHost: false,
        action: null,
        attended: false,
      });
      setInitialParticipantActionSettled(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/events/${eventId}/participant-action/`, {
          authOptional: true,
          suppressUnauthorizedRedirect: true,
          timeout: 12000,
        });
        if (cancelled) return;
        setParticipantPrimaryAction({
          isParticipant: Boolean(data?.is_participant),
          isHost: Boolean(data?.is_host),
          action: data?.primary_action || data?.action || null,
          attended: Boolean(data?.attended),
        });
      } catch {
        if (cancelled) return;
        setParticipantPrimaryAction({
          isParticipant: false,
          isHost: false,
          action: null,
          attended: false,
        });
      } finally {
        if (!cancelled) setInitialParticipantActionSettled(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chatKind, eventId]);

  useEffect(() => {
    const nextMax = Number(eventForCard?.max_participants ?? chat?.max_participants ?? 0);
    if (Number.isFinite(nextMax) && nextMax > 0) {
      setConceptActivationMaxParticipants(nextMax);
      return;
    }
    setConceptActivationMaxParticipants(0);
  }, [chat?.max_participants, eventForCard?.max_participants, eventId]);

  const isHostInEventChat = Boolean(
    participantPrimaryAction?.isHost ||
      isHostRole(myRoleDetected) ||
      chat?.is_host ||
      chat?.i_am_host ||
      eventForCard?.is_host
  );

  useEffect(() => {
    // The explicit deep-link is emitted only by the concept-date action. Do not
    // wait for the event preview to classify the chat as a concept: in the new
    // chat layout that preview can arrive after this effect, leaving the host
    // in chat with the requested editor never opened.
    if (chatKind !== "event" || !eventId || !isHostInEventChat) return;

    let searchParams;
    try {
      searchParams = new URLSearchParams(location?.search || "");
    } catch {
      return;
    }

    if (searchParams.get("concept_poll") !== "1") return;

    setConceptDatePollModalOpen(true);

    searchParams.delete("concept_poll");
    const nextSearch = searchParams.toString();
    navigate(`${location?.pathname || `/menu/chats/event/${eventId}`}${nextSearch ? `?${nextSearch}` : ""}`, {
      replace: true,
    });
  }, [
    chatKind,
    eventId,
    isConceptEvent,
    isHostInEventChat,
    location?.pathname,
    location?.search,
    navigate,
  ]);

  useEffect(() => {
    if (
      chatKind !== "event"
      || !eventId
      || !isConceptEvent
      || !isHostInEventChat
      || conceptActivationMaxParticipants > 0
    ) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/events/details/${eventId}/`);
        if (cancelled) return;
        const nextMax = Number(data?.max_participants ?? 0);
        if (Number.isFinite(nextMax) && nextMax > 0) {
          setConceptActivationMaxParticipants(nextMax);
        }
      } catch {
        // Keep CTA hidden when the capacity is unknown.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    chatKind,
    conceptActivationMaxParticipants,
    eventId,
    isConceptEvent,
    isHostInEventChat,
  ]);

  const feedRef = useRef(null);
  const inputRef = useRef(null);
  const headerRef = useRef(null);
  const inputBarWrapRef = useRef(null);
  const desktopChatModalHostRef = useRef(null);
  const chatViewportStyle = useChatVisualViewportStyle(isDesktop, feedRef);

  // ===== Dynamic bottom padding so the last message is NEVER hidden behind BottomNavigation + ChatInputBar =====
  // On iOS Safari the URL bar can change the visible viewport, and fixed elements may overlap the scroll area.
  // So we compute the REAL overlap between the feed and #bottom-nav, and pad the feed accordingly.
  const [feedBottomPadPx, setFeedBottomPadPx] = useState(16);
  const lastFeedBottomPadRef = useRef(16);

  const recomputeFeedBottomPadding = () => {
    const feed = feedRef.current;
    if (!feed) return;
    // While prepending history we must not mutate bottom padding, otherwise scroll restoration can “bounce”.
    if (isPrependingRef.current) return;

    const MIN_PAD = 16;
    const GAP = 8;

    // Hard safety cap to prevent “infinite scroll” when iOS/Telegram reports weird rects
    // (e.g. keyboard open + VisualViewport shifts).
    const vv = window.visualViewport;
    const viewportTop = vv && Number.isFinite(vv.offsetTop) ? vv.offsetTop : 0;
    const viewportHeight = vv && Number.isFinite(vv.height) ? vv.height : window.innerHeight;
    const viewportBottom = viewportTop + viewportHeight;
    const MAX_PAD = Math.max(MIN_PAD, Math.ceil(viewportHeight * 0.75));

    let pad = MIN_PAD;

    try {
      const feedRect = feed.getBoundingClientRect();

      // Use the visible viewport bottom (not the layout viewport / document) to avoid huge overlaps.
      const feedBottom = Math.min(
        Number.isFinite(feedRect.bottom) ? feedRect.bottom : viewportBottom,
        viewportBottom
      );

      const inputRect = inputBarWrapRef.current?.getBoundingClientRect?.();
      const inputTop =
        inputRect && Number.isFinite(inputRect.top) ? inputRect.top : null;
      const inputH =
        inputRect && Number.isFinite(inputRect.height) ? inputRect.height : 0;

      let coverTop = null;

      // If we have a real input bar rect, use it
      if (inputTop != null && inputH > 0) {
        coverTop = inputTop;
      } else {
        // Fallback to bottom-nav rect
        const nav = document.getElementById("bottom-nav");
        const navRect = nav?.getBoundingClientRect?.();
        if (navRect && Number.isFinite(navRect.top)) {
          coverTop = navRect.top;
        }
      }

      // Sanity-check coverTop: if it’s outside the visible viewport, ignore it.
      if (
        coverTop == null ||
        !Number.isFinite(coverTop) ||
        coverTop < viewportTop - 8 ||
        coverTop > viewportBottom + 8
      ) {
        coverTop = null;
      }

      if (coverTop != null) {
        const overlap = Math.max(0, feedBottom - coverTop);
        pad = Math.max(MIN_PAD, Math.ceil(overlap + GAP));
      } else {
        // Last resort fallback: use known heights if rects aren't available yet
        const approxNav = Math.max(0, Math.min(getBottomNavHeightPx(), viewportHeight));
        const approxInput = Math.max(0, Math.min(getInputBarHeightPx(), viewportHeight));
        const approx = approxNav + approxInput + GAP;
        pad = Math.max(MIN_PAD, Math.ceil(approx));
      }

      // Final safety clamp
      pad = Math.min(pad, MAX_PAD);

      // If something still produced a crazy jump, prefer previous value.
      const prev = lastFeedBottomPadRef.current || MIN_PAD;
      if (pad > prev * 3 && pad > MIN_PAD + 24) {
        pad = Math.min(prev + 48, MAX_PAD);
      }
    } catch {
      // keep previous
      pad = lastFeedBottomPadRef.current || MIN_PAD;
    }

    // Avoid state churn
    if (Math.abs((lastFeedBottomPadRef.current || 0) - pad) >= 2) {
      lastFeedBottomPadRef.current = pad;
      setFeedBottomPadPx(pad);
    }
  };

  // ===== Message actions (context menu) =====
  const [ctxMenu, setCtxMenu] = useState(null);
  const [showParticipants, setShowParticipants] = useState(() => {
    if (isSupportChat) return false;
    if (typeof window === "undefined") return false;
    try {
      const qs = new URLSearchParams(window.location.search || "");
      return qs.get("participants") === "1";
    } catch {
      return false;
    }
  });
  // ctxMenu shape: { item, rect: DOMRect }

  useEffect(() => {
    if (isSupportChat) {
      setShowParticipants(false);
      return;
    }
    try {
      const qs = new URLSearchParams(location?.search || "");
      if (qs.get("participants") === "1") {
        setShowParticipants(true);
      }
    } catch {
      // ignore
    }
  }, [isSupportChat, location?.search]);

  useEffect(() => {
    if (isSupportChat) {
      if (showParticipants) setShowParticipants(false);
      return;
    }
    try {
      const qs = new URLSearchParams(location?.search || "");
      const hasParticipantsParam = qs.get("participants") === "1";
      if (showParticipants === hasParticipantsParam) return;

      if (showParticipants) qs.set("participants", "1");
      else qs.delete("participants");

      navigate(
        {
          pathname: location?.pathname || "",
          search: qs.toString() ? `?${qs.toString()}` : "",
        },
        { replace: true }
      );
    } catch {
      // ignore URL sync errors
    }
  }, [isSupportChat, showParticipants, navigate, location?.pathname, location?.search]);

  const [replyTo, setReplyTo] = useState(null);
  // replyTo: { id, preview }

  const [editingId, setEditingId] = useState(null);
  const chatDraftIdentity = internalChatId ? `${chatKind}:${internalChatId}` : null;
  const {
    clearDraft: clearChatDraft,
    persistDraft: persistChatDraft,
    restoreDraft: restoreChatDraft,
  } = useChatDraftPersistence({
    userId: currentUserId,
    chatIdentity: chatDraftIdentity,
    value: inputValue,
    setValue: setInputValue,
    inputRef,
    disabled: Boolean(editingId || sending),
  });
  const orderReference = isSupportChat
    ? String(location.state?.orderReference || "").trim().slice(0, 128)
    : "";
  useEffect(() => {
    if (!orderReference || !currentUserId || !chatDraftIdentity) return;
    const storageKey = buildChatDraftStorageKey(currentUserId, chatDraftIdentity);
    const existingDraft = String(inputRef.current?.value || inputValue || loadChatDraft(storageKey)).trim();
    if (!existingDraft) {
      restoreChatDraft(t("subscription_concept.orders.support_draft", { reference: orderReference }));
    }
    navigate(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: { ...(location.state || {}), orderReference: undefined } }
    );
  }, [chatDraftIdentity, currentUserId, inputValue, location.hash, location.pathname, location.search, location.state, navigate, orderReference, restoreChatDraft, t]);
  const [editingMeta, setEditingMeta] = useState(null);
  // editingMeta: { id, preview }
  const [flashId, setFlashId] = useState(null);
  const [messageAttentionModalOpen, setMessageAttentionModalOpen] = useState(false);
  const [messageAttentionSelecting, setMessageAttentionSelecting] = useState(false);
  const [messageAttentionSelectedMessageId, setMessageAttentionSelectedMessageId] = useState(null);
  const [messageAttentionSelectedPreview, setMessageAttentionSelectedPreview] = useState("");
  const [messageAttentionSelectedAuthor, setMessageAttentionSelectedAuthor] = useState("");
  const [messageAttentionSelectedMeta, setMessageAttentionSelectedMeta] = useState(null);
  const [messageAttentionSubmitting, setMessageAttentionSubmitting] = useState(false);
  const [messageReportModalOpen, setMessageReportModalOpen] = useState(false);
  const [messageReportSelecting, setMessageReportSelecting] = useState(false);
  const [messageReportSelectedMessageId, setMessageReportSelectedMessageId] = useState(null);
  const [messageReportSelectedPreview, setMessageReportSelectedPreview] = useState("");
  const [messageReportSelectedAuthor, setMessageReportSelectedAuthor] = useState("");
  const [messageReportSelectedMeta, setMessageReportSelectedMeta] = useState(null);
  const [messageReportSubmitting, setMessageReportSubmitting] = useState(false);
  const [participantReportModalOpen, setParticipantReportModalOpen] = useState(false);
  const [participantReportSelecting, setParticipantReportSelecting] = useState(false);
  const [participantReportSelectedParticipant, setParticipantReportSelectedParticipant] = useState(null);
  const [participantReportComment, setParticipantReportComment] = useState("");
  const [participantReportSubmitting, setParticipantReportSubmitting] = useState(false);
  const [chatNotificationsModalOpen, setChatNotificationsModalOpen] = useState(false);
  const [chatNotificationsSubmitting, setChatNotificationsSubmitting] = useState(false);

  // ===== Image viewer overlay (Telegram-like) =====
  const [imageViewer, setImageViewer] = useState(null);
  // imageViewer shape: { src, item }
  const chatOverlayActive = Boolean(
    imageViewer?.src ||
    ctxMenu?.item ||
    messageAttentionSelecting ||
    messageReportSelecting ||
    messageAttentionModalOpen ||
    messageReportModalOpen
  );

  const canRequestMessageAttention = Boolean(
    chatKind === "event" &&
      (chatMeta?.can_request_message_attention || isHostRole(myRoleDetected))
  );
  const messageAttentionRequestsUsed = Number(
    chatMeta?.message_attention_requests_used || 0
  );
  const messageAttentionRequestsLimit = Number(
    chatMeta?.message_attention_requests_limit || DEFAULT_MESSAGE_ATTENTION_LIMIT
  );
  const canCreateMoreMessageAttentionRequests =
    canRequestMessageAttention &&
    messageAttentionRequestsUsed < messageAttentionRequestsLimit;
  const canReportChatMessages = Boolean(
    (chatKind === "event" || chatKind === "group") &&
      !canRequestMessageAttention
  );
  const canToggleChatNotifications = Boolean(notificationChatId);
  const chatNotificationsMuted = Boolean(chatMeta?.notifications_muted);
  const canSendMessages = Boolean(chatMeta?.can_send_messages ?? true);
  const sendDisabledReason =
    chatMeta?.send_disabled_reason || t("chats.group_chat.members_posting_disabled");

  const buildChatMessagePreview = React.useCallback(
    (item, fallbackKey = "chats.event_chat.host_attention.preview_fallback") => {
      const raw = String(item?.text || "").trim();
      if (raw) {
        return raw.length > 180 ? `${raw.slice(0, 179).trimEnd()}…` : raw;
      }
      return t(fallbackKey);
    },
    [t]
  );

  const buildChatMessageAuthorLabel = React.useCallback((item) => {
    if (isDeletedAccountUser(item)) return DELETED_USER_LABEL;

    const username = String(
      item?.sender_username ||
        item?.author_username ||
        item?.username ||
        item?.author?.username ||
        item?.sender?.username ||
        ""
    ).trim();
    if (username) return formatUserHandle(username);

    const code = String(
      item?.sender_code ||
        item?.author_code ||
        item?.code ||
        item?.author?.code ||
        item?.sender?.code ||
        ""
    ).trim();
    if (code) return code.toUpperCase();

    if (item?.is_self) return "Me";

    return "";
  }, []);

  const buildChatMessageSelectedMeta = React.useCallback(
    (item) => {
      const authorLabel = buildChatMessageAuthorLabel(item);
      const code = String(
        item?.sender_code ||
          item?.author_code ||
          item?.code ||
          item?.author?.code ||
          item?.sender?.code ||
          ""
      ).trim();
      const role = String(item?.role || item?.author_role || "").trim().toLowerCase();
      const roleLabel = item?.author_is_staff
        ? t("chats.roles.vaultgrid_admin")
        : role === "system"
          ? t("chats.roles.system")
          : isHostRole(role)
            ? t("chats.roles.host")
            : "";

      return {
        authorLabel,
        codeLabel: code ? code.toUpperCase() : "",
        roleLabel,
        timeLabel: String(item?.time || "").trim(),
      };
    },
    [buildChatMessageAuthorLabel, t]
  );

  const openMessageAttentionModal = React.useCallback(() => {
    setMessageAttentionModalOpen(true);
  }, []);

  const openMessageReportModal = React.useCallback(() => {
    setMessageReportModalOpen(true);
  }, []);

  const openParticipantReportModal = React.useCallback(() => {
    setParticipantReportModalOpen(true);
  }, []);

  const openChatNotificationsModal = React.useCallback(() => {
    if (!canToggleChatNotifications) return;
    setChatNotificationsModalOpen(true);
  }, [canToggleChatNotifications]);

  const closeChatNotificationsModal = React.useCallback(() => {
    if (chatNotificationsSubmitting) return;
    setChatNotificationsModalOpen(false);
  }, [chatNotificationsSubmitting]);

  const submitChatNotificationsToggle = React.useCallback(async () => {
    if (!canToggleChatNotifications || !notificationChatId || chatNotificationsSubmitting) return;

    const nextMuted = !chatNotificationsMuted;
    setChatNotificationsSubmitting(true);
    try {
      const resp = await api.post(`/chats/internal/${notificationChatId}/notifications/`, {
        muted: nextMuted,
      });
      const confirmedMuted = Boolean(resp?.data?.notifications_muted);
      setChatMeta((prev) => ({
        ...prev,
        notifications_muted: confirmedMuted,
      }));
      setChatNotificationsModalOpen(false);
      window.dispatchEvent(new CustomEvent("vg:chat-inbox-updated"));
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: confirmedMuted
              ? t("chats.notifications.disabled_toast")
              : t("chats.notifications.enabled_toast"),
          },
        })
      );
    } catch (error) {
      const backendMessage = error?.response?.data?.error;
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: {
            message:
              backendMessage || t("chats.notifications.update_failed"),
          },
        })
      );
    } finally {
      setChatNotificationsSubmitting(false);
    }
  }, [
    canToggleChatNotifications,
    chatNotificationsMuted,
    chatNotificationsSubmitting,
    notificationChatId,
    t,
  ]);

  // ===== ChatInputBar is rendered inside BottomNavigation (slot) =====
  // IMPORTANT:
  // We must NOT re-fire `vg-chat-input-open` on every keystroke/state change,
  // otherwise BottomNavigation can get stuck in the expanded state.
  // We open the slot once per chat context and close on unmount / participants screen.
  const chatInputSlotOpenedRef = useRef(false);
  const slotChatIdRef = useRef(null);

  const getChatInputSlotProps = () => ({
    // i18n
    t,
    language: i18n.resolvedLanguage || i18n.language || "en",

    // refs
    inputRef,
    fileInputRef,
    wrapRef: inputBarWrapRef,

    // state
    inputValue,
    sending,
    replyTo,
    editingId,
    editingMeta,
    pendingImages,
    unreadCount: chatMeta?.unread_count || 0,
    typingUsers,
    showTypingIndicator: false,
    overlayDimmed: !!ctxMenu?.item,
    primaryCtaLabel: bottomPrimaryCtaLabel,
    onPrimaryCtaClick: bottomPrimaryCtaAction,

    // capabilities
    allowAttachments: true,
    canSendMessages,
    sendDisabledMessage: !canSendMessages ? sendDisabledReason : "",
    leftActionButtons: [],
    supportIdentityChoice: isSupportChat && Boolean(
      chatMeta?.support?.viewer_is_staff_watcher || chat?.viewer_is_staff_watcher
    ),
    sendWithPersonalPhoto,
    onSupportIdentityChange: setSendWithPersonalPhoto,

    // handlers
    onInputChange: handleInputChange,
    onInputKeyDown: handleInputKeyDown,
    onSend: handleSend,
    onSendSticker: (stickerId) => handleSend({ stickerId }),
    onPickImages,
    onOpenNativeImagePicker: openNativeImagePicker,
    onOpenPoll: () => setPollSheetOpen(true),
    onOpenLocation: () => setLocationSheetOpen(true),
    onRemoveImage: removePendingImage,
    onCancelContext: () => {
      const wasEditing = !!editingId;
      setReplyTo(null);
      setEditingId(null);
      setEditingMeta(null);
      if (wasEditing) setInputValue("");
      if (typingStopTimerRef.current) {
        try {
          window.clearTimeout(typingStopTimerRef.current);
        } catch {
          // ignore
        }
        typingStopTimerRef.current = null;
      }
      sendTypingState(false);
    },
    onOverlayDismiss: closeCtxMenu,
    onJumpToUnread: () => {
      try {
        const feed = feedRef.current;
        if (feed) feed.scrollTop = feed.scrollHeight;
      } catch {}
      const lastId = Number(chatMeta?.last_message_id || 0);
      if (lastId > 0) postReadUpTo(lastId);
    },
  });

  const closeChatInputSlot = () => {
    try {
      window.dispatchEvent(new CustomEvent("vg-chat-input-close"));
    } catch {
      // ignore
    }
  };

  const setBottomNavSuppressed = (active) => {
    try {
      window.dispatchEvent(
        new CustomEvent("vg-bottom-nav-suppression", {
          detail: {
            active: !!active,
            source: "event-chat-overlay",
          },
        })
      );
    } catch {
      // ignore
    }
  };

  const openChatInputSlotOnce = () => {
    try {
      window.dispatchEvent(
        new CustomEvent("vg-chat-input-open", {
          detail: {
            props: getChatInputSlotProps(),
            // Hint for BottomNavigation: mount/render the chat input slot
            // but do NOT force the nav into expanded state.
            no_auto_expand: true,
            source: "chat",
          },
        })
      );
    } catch {
      // ignore
    }
  };

  const updateChatInputSlotProps = () => {
    try {
      window.dispatchEvent(
        new CustomEvent("vg-chat-input-update", {
          detail: {
            props: getChatInputSlotProps(),
            // Keep the same hint on updates as well.
            no_auto_expand: true,
            source: "chat",
          },
        })
      );
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    // Desktop renders the input bar inside the chat modal; do not use BottomNavigation slot.
    if (isDesktop) {
      if (chatInputSlotOpenedRef.current) {
        closeChatInputSlot();
        chatInputSlotOpenedRef.current = false;
      }
      slotChatIdRef.current = null;
      return;
    }

    if (chatOverlayActive) {
      if (chatInputSlotOpenedRef.current) {
        closeChatInputSlot();
        chatInputSlotOpenedRef.current = false;
      }
      return;
    }

    if (deferInputUntilReady) {
      if (chatInputSlotOpenedRef.current) {
        closeChatInputSlot();
        chatInputSlotOpenedRef.current = false;
      }
      return;
    }

    // If participants screen is open, remove the input bar from BottomNavigation.
    if (showParticipants) {
      if (chatInputSlotOpenedRef.current) {
        closeChatInputSlot();
        chatInputSlotOpenedRef.current = false;
      }
      return;
    }

    // If we switched to a different chat, re-open the slot with new props.
    if (slotChatIdRef.current !== internalChatId) {
      if (chatInputSlotOpenedRef.current) {
        closeChatInputSlot();
        chatInputSlotOpenedRef.current = false;
      }
      slotChatIdRef.current = internalChatId;
    }

    // Open once (do not re-trigger on typing)
    if (!chatInputSlotOpenedRef.current) {
      openChatInputSlotOnce();
      chatInputSlotOpenedRef.current = true;
    }

    return () => {
      // Ensure cleanup when leaving the chat component
      closeChatInputSlot();
      chatInputSlotOpenedRef.current = false;
      slotChatIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showParticipants,
    internalChatId,
    isDesktop,
    chatOverlayActive,
    deferInputUntilReady,
  ]);

  useEffect(() => {
    const active = !isDesktop && chatOverlayActive;
    setBottomNavSuppressed(active);

    return () => {
      if (active) setBottomNavSuppressed(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, chatOverlayActive]);

  // Update ChatInputBar props inside BottomNavigation WITHOUT re-opening the slot.
  // This keeps focus stable and avoids re-expanding BottomNavigation on every keystroke.
  useEffect(() => {
    if (isDesktop) return;
    if (chatOverlayActive) return;
    if (showParticipants) return;
    if (!chatInputSlotOpenedRef.current) return;

    let raf = null;

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        updateChatInputSlotProps();
      });
    };

    schedule();

    return () => {
      try {
        if (raf) window.cancelAnimationFrame(raf);
      } catch {
        // ignore
      }
      raf = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDesktop,
    imageViewer?.src,
    ctxMenu?.item,
    showParticipants,
    inputValue,
    sending,
    replyTo?.id,
    editingId,
    pendingImages?.length,
    chatMeta?.unread_count,
    typingUsers,
    ctxMenu?.item?.id,
    canRequestMessageAttention,
    messageAttentionSelecting,
    messageAttentionModalOpen,
    messageReportSelecting,
    messageReportModalOpen,
    sendWithPersonalPhoto,
    chatMeta?.support?.viewer_is_staff_watcher,
  ]);

  const getBottomNavHeightPx = () => {
    try {
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue("--bottom-nav-height")
        .trim();
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  };

  const getHeaderBottomPx = () => {
    try {
      const r = headerRef.current?.getBoundingClientRect?.();
      return r?.bottom ?? 0;
    } catch {
      return 0;
    }
  };

  const getInputBarHeightPx = () => {
    try {
      const r = inputBarWrapRef.current?.getBoundingClientRect?.();
      return r?.height ?? 0;
    } catch {
      return 0;
    }
  };

  useEffect(() => {
    if (showParticipants) return;

    let raf = null;

    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        recomputeFeedBottomPadding();
      });
    };

    // Initial + a couple of delayed recalcs to catch Safari UI animations
    schedule();
    const t1 = window.setTimeout(schedule, 80);
    const t2 = window.setTimeout(schedule, 220);

    window.addEventListener("resize", schedule, { passive: true });
    window.addEventListener("orientationchange", schedule, { passive: true });

    const vv = window.visualViewport;
    vv?.addEventListener?.("resize", schedule);
    vv?.addEventListener?.("scroll", schedule);

    // Observe BottomNavigation size changes (ChatInputBar grows/shrinks)
    let roNav = null;
    let roFeed = null;

    try {
      if (typeof ResizeObserver !== "undefined") {
        const nav = document.getElementById("bottom-nav");
        if (nav) {
          roNav = new ResizeObserver(schedule);
          roNav.observe(nav);
        }

        if (feedRef.current) {
          roFeed = new ResizeObserver(schedule);
          roFeed.observe(feedRef.current);
        }
      }
    } catch {
      // ignore
    }

    return () => {
      try {
        if (raf) window.cancelAnimationFrame(raf);
      } catch {
        // ignore
      }
      raf = null;

      try {
        window.clearTimeout(t1);
        window.clearTimeout(t2);
      } catch {
        // ignore
      }

      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      vv?.removeEventListener?.("resize", schedule);
      vv?.removeEventListener?.("scroll", schedule);

      try {
        roNav?.disconnect?.();
      } catch {
        // ignore
      }
      try {
        roFeed?.disconnect?.();
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showParticipants]);

  // Recompute when message list changes (layout can shift)
  useEffect(() => {
    if (showParticipants) return;
    if (isPrependingRef.current) return;
    const id = window.requestAnimationFrame(() => recomputeFeedBottomPadding());
    return () => {
      try {
        window.cancelAnimationFrame(id);
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, showParticipants]);

  // ===== Seen-by panel (for my own messages) =====
  const [seenPanel, setSeenPanel] = useState({
    open: false,
    messageId: null,
    loading: false,
    users: [],
    count: 0,
    error: null,
  });
  // Cache seen counts so context menu shows correct value immediately
  const seenCacheRef = useRef({}); // { [messageId]: { count, users } }

  const closeSeenPanel = () => {
    setSeenPanel({
      open: false,
      messageId: null,
      loading: false,
      users: [],
      count: 0,
      error: null,
    });
  };

  const normalizeSeenUser = (u) => {
    if (!u) return null;

    const code =
      (u?.code || u?.user_code || u?.author_code || u?.profile_code || "").toString();

    const usernameRaw =
      u?.username ||
      u?.user_username ||
      u?.author_username ||
      u?.handle ||
      u?.user?.username ||
      u?.user?.handle ||
      u?.profile?.username ||
      "";

    const username = usernameRaw ? String(usernameRaw) : "";
    const isDeletedUser = isDeletedUsername(username) || isDeletedAccountUser(u);

    // Prefer backend-provided avatar, then our cached map by code
    const avatarSrc = isDeletedUser
      ? null
      : buildAvatarSrc(u) || (code ? avatarByCode?.[code] : null) || null;

    return {
      id: u?.id ?? u?.user_id ?? null,
      user_id: u?.user_id ?? u?.id ?? null,
      code: isDeletedUser ? null : code || null,
      username: isDeletedUser ? DELETED_USER_LABEL : username || null,
      is_self: !!(u?.is_self || u?.is_me || u?.me),
      avatarSrc,
      has_premium_subscription: !isDeletedUser && Boolean(u?.has_premium_subscription),
    };
  };

  const applySeenSnapshot = useCallback((messageId, users, count) => {
    const normalizedId = Number(messageId || 0);
    if (!normalizedId) return;

    const safeUsers = Array.isArray(users) ? users : [];
    const safeCount = Number(count || 0) || 0;

    seenCacheRef.current[String(normalizedId)] = {
      users: safeUsers,
      count: safeCount,
    };

    setItems((prev) =>
      prev.map((item) =>
        Number(item?.id || 0) === normalizedId
          ? { ...item, seen_users: safeUsers, seen_count: safeCount }
          : item
      )
    );

    setCtxMenu((prev) =>
      prev && Number(prev?.item?.id || 0) === normalizedId
        ? {
            ...prev,
            item: {
              ...prev.item,
              seen_users: safeUsers,
              seen_count: safeCount,
            },
          }
        : prev
    );
  }, []);

  const fetchSeenByMessage = async (messageId) => {
    if (!messageId) return { users: [], count: 0 };

    // Endpoint provided by backend:
    // path('internal/messages/<int:message_id>/seen/', ...)
    const resp = await api.get(`/chats/internal/messages/${messageId}/seen/`);
    const data = resp?.data;

    // Be defensive: accept multiple possible payload shapes
    const list =
      (Array.isArray(data?.seen) && data.seen) ||
      (Array.isArray(data?.viewers) && data.viewers) ||
      (Array.isArray(data?.users) && data.users) ||
      (Array.isArray(data?.seen_by) && data.seen_by) ||
      (Array.isArray(data) && data) ||
      [];

    const users = list.map(normalizeSeenUser).filter(Boolean);

    const count =
      Number(data?.count || data?.seen_count || data?.viewers_count || users.length || 0) || 0;

    applySeenSnapshot(messageId, users, count);
    return { users, count };
  };

  const toggleSeenPanel = async (messageId, options = {}) => {
    if (!messageId) return;

    const forceOpen = !!options?.force;
    const cached = seenCacheRef.current[String(messageId)];

    setSeenPanel((prev) => {
      // Toggle off if same message already open
      if (!forceOpen && prev?.open && prev?.messageId === messageId) {
        return {
          open: false,
          messageId: null,
          loading: false,
          users: [],
          count: 0,
          error: null,
        };
      }

      return {
        open: true,
        messageId,
        loading: !cached,
        users: cached?.users || [],
        count: cached?.count || 0,
        error: null,
      };
    });

    try {
      const { users, count } = await fetchSeenByMessage(messageId);
      setSeenPanel((prev) => {
        if (!prev?.open || prev?.messageId !== messageId) return prev;
        return {
          ...prev,
          loading: false,
          users,
          count,
          error: null,
        };
      });
    } catch (e) {
      setSeenPanel((prev) => {
        if (!prev?.open || prev?.messageId !== messageId) return prev;
        return {
          ...prev,
          loading: false,
          users: [],
          error: "failed",
        };
      });
    }
  };


  const openImageViewer = (src, item = null) => {
    if (!src) return;
    // Close context menu if any, so overlays never stack
    closeCtxMenu();
    if (!isDesktop && chatInputSlotOpenedRef.current) {
      closeChatInputSlot();
      chatInputSlotOpenedRef.current = false;
    }
    if (!isDesktop) setBottomNavSuppressed(true);

    // If keyboard is open, close it to avoid covering the viewer
    try {
      inputRef.current?.blur?.();
    } catch {
      // ignore
    }

    setImageViewer({ src: String(src), item });
  };

  const closeImageViewer = () => {
    setImageViewer(null);
  };

  useEffect(() => {
    if (!messagesBaseUrl) return;
    if (!requestedMessageId) return;
    pendingJumpTargetRef.current = requestedMessageId;
    pendingJumpAttemptsRef.current = 0;
    pendingJumpShouldFlashRef.current = true;
  }, [messagesBaseUrl, requestedMessageId]);


  const jumpToMessage = (id) => {
    const targetId = Number(id || 0);
    if (!targetId) return;
    const el = feedRef.current?.querySelector?.(`[data-msg-id="${targetId}"]`);
    if (!el) {
      pendingJumpTargetRef.current = targetId;
      pendingJumpAttemptsRef.current = 0;
      pendingJumpShouldFlashRef.current = true;
      if (hasOlder && !loadingOlder) {
        pendingJumpAttemptsRef.current = 1;
        void loadOlderMessagesRef.current?.();
      }
      return;
    }
    pendingJumpTargetRef.current = null;
    pendingJumpAttemptsRef.current = 0;
    pendingJumpShouldFlashRef.current = false;
    try {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    } catch {
      // ignore
    }
    setFlashId(targetId);
    window.setTimeout(() => setFlashId(null), 1200);
  };

  const canSelectMessageAttentionItem = React.useCallback(
    (item) => {
      if (!item || chatKind !== "event" || !canRequestMessageAttention) return false;
      const authorId = Number(item?.author_id || 0);
      const selfFlag = !!item?.is_self;
      return (
        item?.type === ITEM_TYPES.MESSAGE &&
        String(item?.message_type || "").toLowerCase() === "text" &&
        !item?.is_deleted &&
        (selfFlag || (currentUserId != null && authorId === Number(currentUserId)))
      );
    },
    [canRequestMessageAttention, chatKind, currentUserId]
  );

  const startMessageAttentionSelection = React.useCallback(() => {
    if (!canCreateMoreMessageAttentionRequests) return;
    setShowParticipants(false);
    setMessageAttentionModalOpen(false);
    setMessageReportModalOpen(false);
    setMessageReportSelecting(false);
    setParticipantReportModalOpen(false);
    setParticipantReportSelecting(false);
    setMessageAttentionSelecting(true);
    window.dispatchEvent(
      new CustomEvent("global-info", {
        detail: {
          message: t("chats.event_chat.host_attention.selection_started"),
        },
      })
    );
  }, [canCreateMoreMessageAttentionRequests, t]);

  const canSelectMessageReportItem = React.useCallback(
    (item) => {
      if (!item || !canReportChatMessages) return false;
      const authorId = Number(item?.author_id || 0);
      const selfFlag = !!item?.is_self;
      const messageType = String(item?.message_type || "").toLowerCase();
      return (
        item?.type === ITEM_TYPES.MESSAGE &&
        (messageType === "text" || messageType === "system_as_user") &&
        !item?.is_deleted &&
        authorId > 0 &&
        !selfFlag &&
        (currentUserId == null || authorId !== Number(currentUserId))
      );
    },
    [canReportChatMessages, currentUserId]
  );

  const startMessageReportSelection = React.useCallback(() => {
    if (!canReportChatMessages) return;
    setShowParticipants(false);
    setMessageReportModalOpen(false);
    setMessageAttentionModalOpen(false);
    setMessageAttentionSelecting(false);
    setParticipantReportModalOpen(false);
    setParticipantReportSelecting(false);
    setMessageReportSelecting(true);
    window.dispatchEvent(
      new CustomEvent("global-info", {
        detail: {
          message: t("chats.event_chat.message_report.selection_started"),
        },
      })
    );
  }, [canReportChatMessages, t]);

  const handleSelectMessageAttentionItem = React.useCallback(
    (item) => {
      if (!messageAttentionSelecting) return;
      if (!canSelectMessageAttentionItem(item)) {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              message: t("chats.event_chat.host_attention.selection_only_own"),
            },
          })
        );
        return;
      }
      const nextId = Number(item?.id || 0);
      setMessageAttentionSelectedMessageId(nextId || null);
      setMessageAttentionSelectedPreview(buildChatMessagePreview(item));
      setMessageAttentionSelectedAuthor(buildChatMessageAuthorLabel(item));
      setMessageAttentionSelectedMeta(buildChatMessageSelectedMeta(item));
      setMessageAttentionSelecting(false);
      setMessageAttentionModalOpen(true);
      setFlashId(nextId || null);
      window.setTimeout(() => setFlashId(null), 1200);
    },
    [
      buildChatMessageAuthorLabel,
      buildChatMessageSelectedMeta,
      buildChatMessagePreview,
      canSelectMessageAttentionItem,
      messageAttentionSelecting,
      t,
    ]
  );

  const handleSelectMessageReportItem = React.useCallback(
    (item) => {
      if (!messageReportSelecting) return;
      if (!canSelectMessageReportItem(item)) {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              message: t("chats.event_chat.message_report.selection_only_other"),
            },
          })
        );
        return;
      }
      const nextId = Number(item?.id || 0);
      setMessageReportSelectedMessageId(nextId || null);
      setMessageReportSelectedPreview(
        buildChatMessagePreview(item, "chats.event_chat.message_report.preview_fallback")
      );
      setMessageReportSelectedAuthor(buildChatMessageAuthorLabel(item));
      setMessageReportSelectedMeta(buildChatMessageSelectedMeta(item));
      setMessageReportSelecting(false);
      setMessageReportModalOpen(true);
      setFlashId(nextId || null);
      window.setTimeout(() => setFlashId(null), 1200);
    },
    [
      buildChatMessageAuthorLabel,
      buildChatMessageSelectedMeta,
      buildChatMessagePreview,
      canSelectMessageReportItem,
      messageReportSelecting,
      t,
    ]
  );

  const canSelectActiveMessageItem = React.useCallback(
    (item) => {
      if (messageAttentionSelecting) return canSelectMessageAttentionItem(item);
      if (messageReportSelecting) return canSelectMessageReportItem(item);
      return false;
    },
    [
      canSelectMessageAttentionItem,
      canSelectMessageReportItem,
      messageAttentionSelecting,
      messageReportSelecting,
    ]
  );

  const handleSelectActiveMessageItem = React.useCallback(
    (item) => {
      if (messageAttentionSelecting) {
        handleSelectMessageAttentionItem(item);
        return;
      }
      if (messageReportSelecting) {
        handleSelectMessageReportItem(item);
      }
    },
    [
      handleSelectMessageAttentionItem,
      handleSelectMessageReportItem,
      messageAttentionSelecting,
      messageReportSelecting,
    ]
  );

  const canSelectParticipantReportTarget = React.useCallback(
    (participant) => {
      if (!participant || isDeletedAccountUser(participant)) return false;
      const code = normalizeParticipantCode(participant?.code || participant?.user?.code);
      if (!code) return false;
      if (participant?.is_self || participant?.is_me || participant?.me) return false;
      const userId = getParticipantUserId(participant);
      if (currentUserId != null && userId && userId === Number(currentUserId)) return false;
      return true;
    },
    [currentUserId]
  );

  const startParticipantReportSelection = React.useCallback(() => {
    setShowParticipants(true);
    setParticipantReportModalOpen(false);
    setMessageAttentionModalOpen(false);
    setMessageAttentionSelecting(false);
    setMessageReportModalOpen(false);
    setMessageReportSelecting(false);
    setParticipantReportSelecting(true);
    window.dispatchEvent(
      new CustomEvent("global-info", {
        detail: {
          message: t("chats.event_chat.user_report.selection_started"),
        },
      })
    );
  }, [t]);

  const handleSelectParticipantReportTarget = React.useCallback(
    (participant) => {
      if (!participantReportSelecting) return;
      if (!canSelectParticipantReportTarget(participant)) {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              message: t("chats.event_chat.user_report.selection_only_other"),
            },
          })
        );
        return;
      }
      setParticipantReportSelectedParticipant(participant);
      setParticipantReportSelecting(false);
      setParticipantReportModalOpen(true);
    },
    [canSelectParticipantReportTarget, participantReportSelecting, t]
  );

  const closeMessageAttentionModal = React.useCallback(() => {
    if (messageAttentionSubmitting) return;
    setMessageAttentionModalOpen(false);
    setMessageAttentionSelecting(false);
    setMessageAttentionSelectedMessageId(null);
    setMessageAttentionSelectedPreview("");
    setMessageAttentionSelectedAuthor("");
    setMessageAttentionSelectedMeta(null);
  }, [messageAttentionSubmitting]);

  const closeMessageReportModal = React.useCallback(() => {
    if (messageReportSubmitting) return;
    setMessageReportModalOpen(false);
    setMessageReportSelecting(false);
    setMessageReportSelectedMessageId(null);
    setMessageReportSelectedPreview("");
    setMessageReportSelectedAuthor("");
    setMessageReportSelectedMeta(null);
  }, [messageReportSubmitting]);

  const closeParticipantReportModal = React.useCallback(() => {
    if (participantReportSubmitting) return;
    setParticipantReportModalOpen(false);
    setParticipantReportSelecting(false);
    setParticipantReportSelectedParticipant(null);
    setParticipantReportComment("");
  }, [participantReportSubmitting]);

  const submitMessageAttentionRequest = React.useCallback(async () => {
    if (!eventId || !messageAttentionSelectedMessageId) {
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: {
            message: t("chats.event_chat.host_attention.select_message_first"),
          },
        })
      );
      return;
    }
    setMessageAttentionSubmitting(true);
    try {
      const { data } = await api.post(
        `/chats/${eventId}/internal-chat/message-attention-request/`,
        {
          message_id: messageAttentionSelectedMessageId,
        }
      );

      const nextMessage = data?.message || null;
      if (nextMessage?.id) {
        const messageId = Number(nextMessage.id);
        setItems((prev) =>
          prev.map((item) =>
            Number(item?.id || 0) === messageId
              ? {
                  ...item,
                  attention_request: nextMessage?.attention_request || item?.attention_request || null,
                }
              : item
          )
        );
      }

      setChatMeta((prev) => ({
        ...prev,
        can_request_message_attention: true,
        message_attention_requests_used: Number(
          data?.message_attention_requests_used ?? prev?.message_attention_requests_used ?? 0
        ),
        message_attention_requests_limit: Number(
          data?.message_attention_requests_limit ??
            prev?.message_attention_requests_limit ??
            DEFAULT_MESSAGE_ATTENTION_LIMIT
        ),
      }));

      setMessageAttentionModalOpen(false);
      setMessageAttentionSelecting(false);
      setMessageAttentionSelectedMessageId(null);
      setMessageAttentionSelectedPreview("");
      setMessageAttentionSelectedAuthor("");
      setMessageAttentionSelectedMeta(null);

      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: t("chats.event_chat.host_attention.request_sent"),
          },
        })
      );
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        t("chats.event_chat.host_attention.request_failed");
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message, status },
        })
      );
    } finally {
      setMessageAttentionSubmitting(false);
    }
  }, [eventId, messageAttentionSelectedMessageId, t]);

  const submitMessageReport = React.useCallback(async () => {
    if (!messageReportSelectedMessageId) {
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: {
            message: t("chats.event_chat.message_report.select_message_first"),
          },
        })
      );
      return;
    }
    setMessageReportSubmitting(true);
    try {
      await api.post(
        `/chats/internal-chat/messages/${messageReportSelectedMessageId}/report/`,
        {}
      );

      setMessageReportModalOpen(false);
      setMessageReportSelecting(false);
      setMessageReportSelectedMessageId(null);
      setMessageReportSelectedPreview("");
      setMessageReportSelectedAuthor("");
      setMessageReportSelectedMeta(null);

      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: t("chats.event_chat.message_report.report_sent"),
          },
        })
      );
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        t("chats.event_chat.message_report.report_failed");
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message, status },
        })
      );
    } finally {
      setMessageReportSubmitting(false);
    }
  }, [messageReportSelectedMessageId, t]);

  const submitParticipantReport = React.useCallback(async () => {
    const code = normalizeParticipantCode(
      participantReportSelectedParticipant?.code ||
        participantReportSelectedParticipant?.user?.code
    );
    const description = String(participantReportComment || "").trim();

    if (!code) {
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: {
            message: t("chats.event_chat.user_report.select_user_first"),
          },
        })
      );
      return;
    }

    if (!description) {
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: {
            message: t("chats.event_chat.user_report.description_required"),
          },
        })
      );
      return;
    }

    setParticipantReportSubmitting(true);
    try {
      const { data } = await api.post("/accounts/report-user/", {
        code,
        description,
      });

      setParticipantReportModalOpen(false);
      setParticipantReportSelecting(false);
      setParticipantReportSelectedParticipant(null);
      setParticipantReportComment("");

      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message:
              data?.status === "already_reported"
                ? t("chats.event_chat.user_report.already_reported")
                : t("chats.event_chat.user_report.report_sent"),
          },
        })
      );
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        t("chats.event_chat.user_report.report_failed");
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message, status },
        })
      );
    } finally {
      setParticipantReportSubmitting(false);
    }
  }, [participantReportComment, participantReportSelectedParticipant, t]);

  const optimisticallyClearUnreadSummary = useCallback(
    (upToId) => {
      const targetId = Number(upToId || 0);
      if (!targetId) return;

      try {
        queryClient.setQueryData(["chat_unread_summary"], (current) => {
          if (!current || !Array.isArray(current?.chats)) return current;

          let clearedUnread = 0;
          let changed = false;
          const chats = current.chats.map((entry) => {
            const matchesOpenChat =
              chatKind === "event"
                ? String(entry?.event_id || "") === String(eventId || "")
                : String(entry?.chat_id || "") === String(internalChatId || "");
            if (!matchesOpenChat) return entry;

            const entryUnread = Math.max(0, Number(entry?.unread_count || 0));
            const entryLastMessageId = Number(entry?.last_message_id || 0);
            if (entryLastMessageId > 0 && targetId < entryLastMessageId) return entry;
            if (entryUnread <= 0 && Number(entry?.last_read_message_id || 0) >= entryLastMessageId) {
              return entry;
            }

            clearedUnread += entryUnread;
            changed = true;
            return {
              ...entry,
              unread_count: 0,
              last_read_message_id: Math.max(
                Number(entry?.last_read_message_id || 0),
                targetId
              ),
            };
          });

          if (!changed) return current;
          return {
            ...current,
            total_unread: Math.max(
              0,
              Number(current?.total_unread || 0) - clearedUnread
            ),
            chats,
          };
        });
      } catch {
        // The chat-local acknowledgement still applies if the shared cache is unavailable.
      }
    },
    [chatKind, eventId, internalChatId, queryClient]
  );

  // ===== Mark read up to message_id (backend pointer) =====
  const postReadUpTo = useCallback(async (upToId) => {
    const baseUrl = messagesBaseUrlRef.current || messagesBaseUrl;
    if (!baseUrl) return;
    if (!upToId || upToId <= 0) return;

    // avoid spamming backend / regressions
    if (upToId <= lastSentReadUpToRef.current) return;

    // Record this before awaiting the request. A user can leave and reopen the
    // chat before the backend response is visible to the next GET.
    rememberChatRead(baseUrl, upToId);

    // Reading is a visual acknowledgement, so clear a caught-up badge
    // immediately. The request below remains the source of truth and the
    // unread summary is revalidated after it succeeds.
    setChatMeta((prev) => reconcileChatReadPosition(baseUrl, prev));
    optimisticallyClearUnreadSummary(upToId);

    const form = new FormData();
    form.append('message_id', String(upToId));

    try {
      const resp = await api.post(`${baseUrl}/read-up-to/`, form, {
        meta: { suppressGlobalError: true },
      });
      const payload = resp?.data || {};

      // A response from the previously opened chat must not poison the read
      // pointer of the chat that is currently on screen.
      if (messagesBaseUrlRef.current && messagesBaseUrlRef.current !== baseUrl) return;

      lastSentReadUpToRef.current = Math.max(lastSentReadUpToRef.current, upToId);

      setChatMeta((prev) => ({
        ...prev,
        last_read_message_id: Number(payload?.last_read_message_id || prev.last_read_message_id || 0),
        unread_count: Number(payload?.unread_count || 0),
      }));

      // Keep unread badges in sync across the app (Menu / BottomNav etc.)
      try {
        queryClient.invalidateQueries({ queryKey: ["chat_unread_summary"] });
      } catch {
        // ignore
      }
    } catch {
      // best-effort
    }
  }, [messagesBaseUrl, optimisticallyClearUnreadSummary, queryClient]);

  const scheduleReadUpTo = (upToId) => {
    if (!upToId || upToId <= 0) return;

    // only useful when there are unread messages
    if ((chatMeta?.unread_count || 0) <= 0) return;

    // The visual acknowledgement is immediate; retain it even though the
    // network write below is deliberately debounced.
    rememberChatRead(messagesBaseUrlRef.current || messagesBaseUrl, upToId);

    if (readUpToTimerRef.current) return;

    readUpToTimerRef.current = window.setTimeout(() => {
      readUpToTimerRef.current = null;
      postReadUpTo(upToId);
    }, 700);
  };

  const computeMaxVisibleMessageId = () => {
    const feed = feedRef.current;
    if (!feed) return 0;

    const cRect = feed.getBoundingClientRect();
    const nodes = feed.querySelectorAll('[data-msg-id]');

    let maxId = 0;
    nodes.forEach((el) => {
      const r = el.getBoundingClientRect();
      const visible = r.bottom > cRect.top && r.top < cRect.bottom;
      if (!visible) return;

      const id = Number(el.getAttribute('data-msg-id') || 0);
      if (id > maxId) maxId = id;
    });

    return maxId;
  };

  const loadOlderMessages = async () => {
    if (!messagesBaseUrl) return;
    if (loadingOlder) return;
    if (isPrependingRef.current) return;
    if (!hasOlder) return;

    let prependPosition = null;

    // System messages share the same timeline and must participate in the
    // pagination boundary too, otherwise the edge page can overlap.
    const beforeId = findOldestChatItemId(items);

    if (!beforeId) return;

    try {
      setLoadingOlder(true);
      isPrependingRef.current = true;

      const resp = await api.get(`${messagesBaseUrl}/messages/`, {
        params: { before_id: beforeId, limit: pendingJumpTargetRef.current ? 200 : PAGE_LIMIT },
      });

      const serverMessages = resp?.data?.messages || [];
      const meta = resp?.data?.meta || {};
      setHasOlder(!!meta?.has_older);

      if (!serverMessages.length) return;

      const mappedOlder = serverMessages.map((m) => {
        if (m.message_type === "system") {
          const localizedText = getViewerLocalizedSystemText(m, t);
          return {
            type: ITEM_TYPES.SYSTEM,
            id: m.id,
            text: localizedText,
            payload: m.payload || null,
            lines: [localizedText],
          };
        }

        // Special: system message rendered as a normal bubble from a virtual user (VG)
        if (m.message_type === "system_as_user") {
          return {
            type: ITEM_TYPES.MESSAGE,
            id: m.id,
            created_at: m.created_at || null,
            author_id: null,
            message_type: "system_as_user",
            text: m.text,
            payload: m.payload || null,
            time: formatShortTime(m.created_at),
            role: SYSTEM_USER_ROLE,
            is_self: false,
            sender_username: SYSTEM_USER_USERNAME,
            sender_code: SYSTEM_USER_CODE,
            edited_at: m.edited_at || null,
            reply_to_id: null,
            reply_to: null,
            attachments: [],
            reactions: normalizeMessageReactions(m.reactions, currentUserId),
          };
        }

        const rawAtt = Array.isArray(m.attachments) ? m.attachments : [];
        const attachments = rawAtt
          .map((a) => {
            const t = a?.type || a?.attachment_type || null;
            const url = a?.url || a?.file_url || a?.file || a?.external_url || null;
            if (!t || !url) return null;
            return {
              type: t,
              url: toApiAbsoluteUrl(url) || url,
              width: a?.width ?? null,
              height: a?.height ?? null,
              original_name: a?.original_name ?? null,
              size_bytes: a?.size_bytes ?? null,
            };
          })
          .filter(Boolean);

        return {
          type: ITEM_TYPES.MESSAGE,
          id: m.id,
          created_at: m.created_at || null,
          author_id: m.author_id ?? m.author?.id ?? null,
          message_type: m.message_type || "text",
          text: m.text,
          payload: m.payload || null,
          time: formatShortTime(m.created_at),
          role: m.author_role || null,
          author_is_staff: !!m.author_is_staff,
          author_is_deleted_account: !!m.author_is_deleted_account,
          is_self: !!m.is_self,
          sender_username: m.author_username || "",
          sender_code: m.author_code || null,
          avatarSrc: extractAvatarFromMessage(m),
          sender_has_premium_subscription: !!m.author_has_premium_subscription,
          edited_at: m.edited_at || null,
          reply_to_id: m.reply_to_id || null,
          reply_to: m.reply_to || null,
          attachments,
          reactions: normalizeMessageReactions(m.reactions, currentUserId),
        };
      });

      // Обновляем карту аватаров и для старых сообщений
      const avatarMapFromMessages = {};
      mappedOlder.forEach((item, idx) => {
        if (item.type !== ITEM_TYPES.MESSAGE) return;

        const src = extractAvatarFromMessage(serverMessages[idx]);
        const code = item.sender_code;
        if (code && src) {
          avatarMapFromMessages[code] = src;
        }
      });

      if (Object.keys(avatarMapFromMessages).length) {
        setAvatarByCode((prev) => ({
          ...prev,
          ...avatarMapFromMessages,
          [SYSTEM_USER_CODE]: SYSTEM_USER_AVATAR_SRC,
          [SUPPORT_USER_CODE]: SUPPORT_USER_AVATAR_SRC,
        }));
      }

      // prepend: старые сообщения идут выше уже загруженных
      prependPosition = captureChatPrependPosition(feedRef.current);
      // React must commit and restore the old visual anchor before the browser
      // paints; a two-frame delay is visible as a full-feed flicker.
      flushSync(() => {
        setItems((prev) => prependOlderChatPage(prev, mappedOlder));
      });
      restoreChatPrependPosition(feedRef.current, prependPosition);
      isPrependingRef.current = false;

      // Now it is safe to recompute bottom padding (avoid doing it during prepend)
      try {
        recomputeFeedBottomPadding();
      } catch {
        // ignore
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to load older chat messages", err);
    } finally {
      setLoadingOlder(false);
      isPrependingRef.current = false;
    }
  };
  loadOlderMessagesRef.current = loadOlderMessages;

  useEffect(() => {
    const targetId = Number(pendingJumpTargetRef.current || 0);
    if (!targetId) return;

    const el = feedRef.current?.querySelector?.(`[data-msg-id="${targetId}"]`);
    if (el) {
      const shouldFlash = pendingJumpShouldFlashRef.current;
      pendingJumpTargetRef.current = null;
      pendingJumpAttemptsRef.current = 0;
      pendingJumpShouldFlashRef.current = false;
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        // ignore
      }
      if (shouldFlash) {
        setFlashId(targetId);
        window.setTimeout(() => setFlashId(null), 1200);
      }
      return;
    }

    if (loadingOlder) return;

    if (!hasOlder || pendingJumpAttemptsRef.current >= 100) {
      pendingJumpTargetRef.current = null;
      pendingJumpAttemptsRef.current = 0;
      pendingJumpShouldFlashRef.current = false;
      return;
    }

    pendingJumpAttemptsRef.current += 1;
    void loadOlderMessagesRef.current?.();
  }, [items, hasOlder, loadingOlder]);

    const onFeedScroll = () => {
      if (rafScrollRef.current) return;
      rafScrollRef.current = window.requestAnimationFrame(() => {
        rafScrollRef.current = null;

        const maxVisibleId = computeMaxVisibleMessageId();
        if (maxVisibleId > 0) scheduleReadUpTo(maxVisibleId);

        // Infinite scroll вверх: если пользователь почти у верхней границы — подгружаем историю
        try {
          const feed = feedRef.current;
          if (
            feed &&
            feed.scrollTop <= 8 &&
            hasOlder &&
            !loadingOlder &&
            !isPrependingRef.current
          ) {
            loadOlderMessages();
          }
        } catch {
          // ignore
        }
      });
    };

  useEffect(() => {
    return () => {
      try {
        if (rafScrollRef.current) window.cancelAnimationFrame(rafScrollRef.current);
      } catch {
        // ignore
      }
      rafScrollRef.current = null;

      try {
        if (readUpToTimerRef.current) window.clearTimeout(readUpToTimerRef.current);
      } catch {
        // ignore
      }
      readUpToTimerRef.current = null;
    };
  }, []);

  // Keep refs in sync for unmount cleanup
  useEffect(() => {
    messagesBaseUrlRef.current = messagesBaseUrl;
  }, [messagesBaseUrl]);

  useEffect(() => {
    pendingJumpTargetRef.current = requestedMessageId || null;
    pendingJumpAttemptsRef.current = 0;
    pendingJumpShouldFlashRef.current = Boolean(requestedMessageId);
    setMessageAttentionModalOpen(false);
    setMessageAttentionSelecting(false);
    setMessageAttentionSelectedMessageId(null);
    setMessageAttentionSelectedPreview("");
    setMessageAttentionSelectedAuthor("");
    setMessageAttentionSelectedMeta(null);
    setMessageAttentionSubmitting(false);
    setMessageReportModalOpen(false);
    setMessageReportSelecting(false);
    setMessageReportSelectedMessageId(null);
    setMessageReportSelectedPreview("");
    setMessageReportSelectedAuthor("");
    setMessageReportSelectedMeta(null);
    setMessageReportSubmitting(false);
    setParticipantReportModalOpen(false);
    setParticipantReportSelecting(false);
    setParticipantReportSelectedParticipant(null);
    setParticipantReportComment("");
    setParticipantReportSubmitting(false);
    setChatNotificationsModalOpen(false);
    setChatNotificationsSubmitting(false);
  }, [messagesBaseUrl, pushUnreadFocusKey, requestedMessageId]);

  useEffect(() => {
    lastMessageIdRef.current = Number(chatMeta?.last_message_id || 0);
  }, [chatMeta?.last_message_id]);

  // Flush read pointer when leaving the chat in any way (not only via Back button)
  useEffect(() => {
    return () => {
      const baseUrl = messagesBaseUrlRef.current;
      const lastId = Number(lastMessageIdRef.current || 0);

      // Best-effort: mark all as read up to the last known message
      if (baseUrl && lastId > 0 && lastId > lastSentReadUpToRef.current) {
        try {
          const form = new FormData();
          form.append("message_id", String(lastId));

          api
            .post(`${baseUrl}/read-up-to/`, form, {
              meta: { suppressGlobalError: true },
            })
            .then((resp) => {
              const payload = resp?.data || {};
              lastSentReadUpToRef.current = Math.max(lastSentReadUpToRef.current, lastId);
              setChatMeta((prev) => ({
                ...prev,
                last_read_message_id: Number(
                  payload?.last_read_message_id || prev?.last_read_message_id || 0
                ),
                unread_count: Number(payload?.unread_count || 0),
              }));
              try {
                queryClient.invalidateQueries({ queryKey: ["chat_unread_summary"] });
              } catch {
                // ignore
              }
            })
            .catch(() => {
              // ignore
            });
        } catch {
          // ignore
        }
      } else {
        // Still refresh global unread summary so badges disappear
        try {
          queryClient.invalidateQueries({ queryKey: ["chat_unread_summary"] });
        } catch {
          // ignore
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Best-effort role detection (host can delete any message)
  const myRole =
    myRoleDetected ||
    chat?.my_role ||
    chat?.role ||
    chat?.member_role ||
    chat?.event?.my_role ||
    chat?.event?.role ||
    chat?.event?.member_role ||
    null;

  const iAmHost = isHostRole(myRole) || !!chat?.is_host || !!chat?.i_am_host;
  const iAmChatModerator = !!chatMeta?.can_moderate_messages;
  const assistantState = chatMeta?.assistant_state || chat?.assistant_state || null;
  const canManageGroupBot = chatKind === "group" && !!assistantState?.can_manage_bot;
  const isGroupBotEnabled = chatKind === "group" && !!assistantState?.bot_enabled;

  const handleGroupBotToggle = useCallback(async () => {
    if (chatKind !== "group" || !internalChatId || !canManageGroupBot || isGroupBotToggleLoading) {
      return;
    }

    const nextEnabled = !isGroupBotEnabled;
    setIsGroupBotToggleLoading(true);
    try {
      const resp = await api.post(`/chats/groups/${internalChatId}/bot-control/`, {
        enabled: nextEnabled,
      });
      const nextState = resp?.data?.assistant_state || null;
      setChatMeta((prev) => ({
        ...prev,
        assistant_state: nextState || prev?.assistant_state || null,
      }));
    } catch {
      // ignore for now
    } finally {
      setIsGroupBotToggleLoading(false);
    }
  }, [
    canManageGroupBot,
    chatKind,
    internalChatId,
    isGroupBotEnabled,
    isGroupBotToggleLoading,
  ]);

  const closeCtxMenu = () => {
    setCtxMenu(null);
    closeSeenPanel();
  };

  const applyReactionSnapshot = useCallback(
    (messageId, rawReactions) => {
      const normalizedId = Number(messageId || 0);
      if (!normalizedId) return;

      const reactions = normalizeMessageReactions(rawReactions, currentUserId);
      setItems((prev) =>
        prev.map((item) =>
          Number(item?.id || 0) === normalizedId ? { ...item, reactions } : item
        )
      );
      setCtxMenu((prev) =>
        prev && Number(prev?.item?.id || 0) === normalizedId
          ? { ...prev, item: { ...prev.item, reactions } }
          : prev
      );
    },
    [currentUserId]
  );

  const isAssistantBotMessage = useCallback(
    (item) => {
      if (chatKind !== "group" || !item) return false;
      if (item?.payload?.assistant_generated) return true;
      return item?.sender_code === "BOT" || item?.sender_username === "VG_bot";
    },
    [chatKind]
  );


  const handleBackClick = () => {
    // Mark that we should reopen ChatModal when returning to Menu
    try {
      sessionStorage.setItem("vg_open_chat_modal_after_back", "1");
      sessionStorage.setItem("vg_open_chat_modal_tab", modalTabKey);
      if (chat?.id) {
        sessionStorage.setItem("vg_open_chat_modal_chat_id", String(chat.id));
      } else if (internalChatId) {
        sessionStorage.setItem("vg_open_chat_modal_chat_id", String(internalChatId));
      }
    } catch {
      // ignore storage errors (Safari private mode, etc.)
    }

    // Always close context menu if any
    closeCtxMenu();
    closeImageViewer();
    sendTypingState(false);

    // Ensure input slot is closed when navigating back
    closeChatInputSlot();

    if (showParticipants) {
      setShowParticipants(false);
      return;
    }

    // Flush read state before leaving (scheduleReadUpTo may not fire if user exits quickly)
    try {
      const lastId = Number(chatMeta?.last_message_id || 0);
      if (lastId > 0) postReadUpTo(lastId);
    } catch {
      // ignore
    }

    // Also refresh global unread summary immediately so badges disappear
    try {
      queryClient.invalidateQueries({ queryKey: ["chat_unread_summary"] });
    } catch {
      // ignore
    }

    onBack?.();
  };

  const openCtxMenu = ({
    item,
    anchorEl,
    rowEl,
    openDetailPanel = null,
    reactionEmoji = null,
  }) => {
    if (!item || !anchorEl) return;

    // If keyboard is open, close it to avoid covering the actions menu
    try {
      inputRef.current?.blur?.();
    } catch {
      // ignore
    }
    if (!isDesktop && chatInputSlotOpenedRef.current) {
      closeChatInputSlot();
      chatInputSlotOpenedRef.current = false;
    }
    if (!isDesktop) setBottomNavSuppressed(true);

    // Opening a new ctx menu should reset the Seen panel state
    closeSeenPanel();

    // anchorEl should be the *bubble* element; rowEl is the whole message row (avatar + bubble)
    const bubbleRect = anchorEl.getBoundingClientRect();
    const rowRect = (rowEl || anchorEl).getBoundingClientRect();
    const overlayScopeEl = anchorEl.closest?.("[data-chat-overlay-scope]");
    const overlayScopeRect = overlayScopeEl?.getBoundingClientRect?.();
    const overlayFeedRect = feedRef.current?.getBoundingClientRect?.();
    const overlayBodyRect = overlayScopeEl
      ?.querySelector?.("[data-chat-overlay-body]")
      ?.getBoundingClientRect?.();
    const overlayHeaderEl = overlayScopeEl?.querySelector?.("[data-chat-overlay-header]");
    const overlayHeaderRect = overlayHeaderEl?.getBoundingClientRect?.();
    const overlayHeaderBorderRadius = overlayHeaderEl
      ? window.getComputedStyle(overlayHeaderEl).borderRadius
      : null;

    const isAssistantMessage = isAssistantBotMessage(item);
    const canViewSeen = Boolean(
      !isSupportChat &&
        (item?.is_self || (iAmChatModerator && isAssistantMessage))
    );

    setCtxMenu({
      item: {
        ...item,
        can_view_seen: canViewSeen,
        is_assistant_bot_message: isAssistantMessage,
        seen_users: item?.seen_users || [],
        avatarSrc: item?.sender_code
          ? avatarByCode?.[item.sender_code] || null
          : null,
      },
      bubbleRect,
      rowRect,
      overlayScopeRect: overlayScopeRect || null,
      overlayFeedRect: overlayFeedRect || null,
      overlayBodyRect: overlayBodyRect || null,
      overlayHeaderRect: overlayHeaderRect || null,
      overlayHeaderBorderRadius,
      openDetailPanel,
      reactionEmoji,
    });

    // Warm seen count so ctx menu doesn't show 0
    if (canViewSeen && item?.id) {
      const cached = seenCacheRef.current[String(item.id)];
      if (cached?.count != null) {
        setCtxMenu((prev) =>
          prev
            ? {
                ...prev,
                item: {
                  ...prev.item,
                  seen_users: cached.users || [],
                  seen_count: cached.count,
                },
              }
            : prev
        );
      }

      // Revalidate every time the menu opens so the count is fresh.
      fetchSeenByMessage(item.id).catch(() => {});
    }
  };

  useEffect(() => {
    const id = ctxMenu?.item?.id;
    if (!id) return;

    let raf = null;

    const updateRect = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;

        const rowEl = feedRef.current?.querySelector?.(`[data-msg-id="${id}"]`);
        const bubbleEl =
          feedRef.current?.querySelector?.(`[data-bubble-id="${id}"]`) || rowEl;

        if (!rowEl || !bubbleEl) return;

        const rowRect = rowEl.getBoundingClientRect();
        const bubbleRect = bubbleEl.getBoundingClientRect();
        const overlayScopeEl = bubbleEl.closest?.("[data-chat-overlay-scope]");
        const overlayScopeRect = overlayScopeEl?.getBoundingClientRect?.();
        const overlayFeedRect = feedRef.current?.getBoundingClientRect?.();
        const overlayBodyRect = overlayScopeEl
          ?.querySelector?.("[data-chat-overlay-body]")
          ?.getBoundingClientRect?.();
        const overlayHeaderEl = overlayScopeEl?.querySelector?.("[data-chat-overlay-header]");
        const overlayHeaderRect = overlayHeaderEl?.getBoundingClientRect?.();
        const overlayHeaderBorderRadius = overlayHeaderEl
          ? window.getComputedStyle(overlayHeaderEl).borderRadius
          : null;

        setCtxMenu((prev) => {
          if (!prev || prev?.item?.id !== id) return prev;
          return {
            ...prev,
            rowRect,
            bubbleRect,
            overlayScopeRect: overlayScopeRect || prev.overlayScopeRect || null,
            overlayFeedRect: overlayFeedRect || prev.overlayFeedRect || null,
            overlayBodyRect: overlayBodyRect || prev.overlayBodyRect || null,
            overlayHeaderRect: overlayHeaderRect || prev.overlayHeaderRect || null,
            overlayHeaderBorderRadius:
              overlayHeaderBorderRadius || prev.overlayHeaderBorderRadius || null,
          };
        });
      });
    };

    updateRect();

    const vv = window.visualViewport;
    vv?.addEventListener?.("resize", updateRect);
    vv?.addEventListener?.("scroll", updateRect);
    window.addEventListener?.("resize", updateRect);

    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      vv?.removeEventListener?.("resize", updateRect);
      vv?.removeEventListener?.("scroll", updateRect);
      window.removeEventListener?.("resize", updateRect);
    };
  }, [ctxMenu?.item?.id]);

  const copyMessage = async (text) => {
    const value = (text || "").toString();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch (e) {
      // fallback
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        // ignore
      }
    }
  };

  const beginReply = (item) => {
    if (!item?.id) return;

    const rawAuthor = (item.sender_username || item.author_username || "").toString();
    const author =
      isDeletedUsername(rawAuthor) || isDeletedAccountUser(item)
        ? DELETED_USER_LABEL
        : formatUserHandle(rawAuthor);
    const raw = (item.text || "").toString();

    // Telegram-like: single-line snippet (remove newlines, collapse whitespace)
    const compact = raw.replace(/\s+/g, " ").trim();
    const preview = compact.length > 70 ? `${compact.slice(0, 70)}…` : compact;

    setReplyTo({ id: item.id, preview, author });
    setEditingId(null);
    setEditingMeta(null);
    closeCtxMenu();
    setTimeout(() => inputRef.current?.focus?.(), 0);
  };

  const beginEdit = (item) => {
    if (!item?.id || isChatPollPayload(item?.payload)) return;

    const raw = (item.text || "").toString();
    const compact = raw.replace(/\s+/g, " ").trim();
    const preview = compact.length > 70 ? `${compact.slice(0, 70)}…` : compact;

    setEditingId(item.id);
    setEditingMeta({ id: item.id, preview });
    setReplyTo(null);
    closeCtxMenu();
    setInputValue(raw);

    setTimeout(() => {
      inputRef.current?.focus?.();
      // Высоту теперь регулирует ChatInputBar через свой useEffect
    }, 0);
  };

  const canEdit = (item) => {
    if (isChatPollPayload(item?.payload)) return false;
    if (item?.is_self) return true;
    return !!(iAmHost || iAmChatModerator) && isAssistantBotMessage(item);
  };
  const canDelete = (item) =>
    !!item?.is_self || iAmHost || iAmChatModerator || currentUserIsSuperuser;

  const togglePinnedMessage = async (item) => {
    if (!item?.id) return;
    try {
      const { data } = await api.post(`/chats/internal-chat/messages/${item.id}/pin/`);
      setChatMeta((prev) => applyChatPinnedMessages(prev, data));
    } catch (err) {
      window.dispatchEvent(new CustomEvent("global-error", {
        detail: {
          message: err?.response?.data?.code === "pin_limit_reached"
            ? t("chats.event_chat.pinned.limit")
            : err?.response?.data?.error || t("chats.event_chat.pinned.failed"),
          status: err?.response?.status,
        },
      }));
    }
  };

  const deleteMessage = async (item) => {
    if (!item?.id) return false;
    try {
      // Backend route: /chats/internal-chat/messages/<id>/delete/
      await api.post(`/chats/internal-chat/messages/${item.id}/delete/`);
      setItems((prev) => prev.filter((m) => m?.id !== item.id));
      setChatMeta((prev) => removeChatPinnedMessage(prev, item.id));
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to delete chat message", err);
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: {
            message:
              err?.response?.data?.error ||
              t("chats.event_chat.ctx.delete_failed", {
                defaultValue: "Could not delete this message.",
              }),
            status: err?.response?.status,
          },
        })
      );
      return false;
    } finally {
      closeCtxMenu();
    }
  };

  const toggleReaction = useCallback(
    async (item, emoji) => {
      const messageId = Number(item?.id || 0);
      if (!messageId || !emoji) return;

      try {
        const resp = await api.post(`/chats/internal-chat/messages/${messageId}/react/`, {
          emoji,
        });
        const payload = resp?.data?.message;
        if (payload?.id) {
          applyReactionSnapshot(payload.id, payload.reactions);
        }
      } catch (err) {
        const status = err?.response?.status;
        const message =
          err?.response?.data?.error ||
          t("chats.event_chat.reactions.update_failed");

        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: { message, status },
          })
        );
      }
    },
    [applyReactionSnapshot, t]
  );

  const handleOpenLocationMessage = useCallback((item) => {
    const messageId = Number(item?.id || 0);
    if (!messageId) return;
    setLocationMapMessageId(messageId);
  }, []);

  const handleVotePoll = useCallback(
    async (item, optionId) => {
      const messageId = Number(item?.id || 0);
      const cleanOptionId = String(optionId || "").trim();
      if (!messageId || !cleanOptionId) return;

      try {
        const resp = await api.post(`/chats/internal-chat/messages/${messageId}/poll-vote/`, {
          option_id: cleanOptionId,
        });
        const payload = resp?.data?.message;
        if (payload?.id) {
          setItems((prev) =>
            prev.map((message) =>
              Number(message?.id || 0) === Number(payload.id)
                ? {
                    ...message,
                    payload: payload.payload || message.payload || null,
                    reactions:
                      payload.reactions != null
                        ? normalizeMessageReactions(payload.reactions, currentUserId)
                        : message.reactions || [],
                  }
                : message
            )
          );
        }
      } catch (err) {
        const status = err?.response?.status;
        const message =
          err?.response?.data?.error ||
          "Poll vote was not updated. Please try again.";

        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: { message, status },
          })
        );
      }
    },
    [currentUserId]
  );

  const clearTypingUsers = useCallback(() => {
    const timers = typingTimersRef.current || {};
    Object.values(timers).forEach((tid) => {
      try {
        window.clearTimeout(tid);
      } catch {
        // ignore
      }
    });
    typingTimersRef.current = {};
    setTypingUsers([]);
  }, []);

  const upsertTypingUser = useCallback(
    (rawUser, isTyping) => {
      const userId = Number(rawUser?.id || 0);
      if (!userId) return;
      if (
        currentUserIdRef.current != null &&
        userId === Number(currentUserIdRef.current)
      ) return;

      const nextUser = {
        id: userId,
        username: rawUser?.username || null,
        code: rawUser?.code || null,
        avatar: rawUser?.avatar || null,
        avatar_custom_url: rawUser?.avatar_custom_url
          ? toApiAbsoluteUrl(rawUser.avatar_custom_url)
          : null,
        avatar_url: rawUser?.avatar_url ? toApiAbsoluteUrl(rawUser.avatar_url) : null,
      };

      const key = String(userId);
      const timers = typingTimersRef.current || {};
      if (timers[key]) {
        try {
          window.clearTimeout(timers[key]);
        } catch {
          // ignore
        }
        delete timers[key];
      }

      if (!isTyping) {
        setTypingUsers((prev) => prev.filter((u) => Number(u?.id || 0) !== userId));
        typingTimersRef.current = timers;
        return;
      }

      setTypingUsers((prev) => {
        const rest = prev.filter((u) => Number(u?.id || 0) !== userId);
        return [...rest, nextUser];
      });

      timers[key] = window.setTimeout(() => {
        setTypingUsers((prev) => prev.filter((u) => Number(u?.id || 0) !== userId));
        delete typingTimersRef.current[key];
      }, 4500);
      typingTimersRef.current = timers;
    },
    []
  );

  const refreshBuddyState = useCallback(async () => {
    if (chatKind !== "group" || !internalChatId) return null;
    try {
      const response = await api.get(`/chats/groups/${internalChatId}/buddy-state/`);
      const nextState = response?.data?.buddy_state || null;
      if (nextState) {
        setChatMeta((prev) => ({ ...prev, buddy_state: nextState }));
      }
      return nextState;
    } catch {
      return null;
    }
  }, [chatKind, internalChatId]);

  useEffect(() => {
    const buddyEventId = Number(chatMeta?.buddy_state?.event?.id || 0);
    if (chatKind !== "group" || !buddyEventId) return undefined;

    const refreshAfterAttendance = (event) => {
      const confirmedEventId = Number(event?.detail?.eventId || event?.detail?.meta?.event_id || 0);
      if (confirmedEventId === buddyEventId) void refreshBuddyState();
    };
    const refreshAfterNotification = (event) => {
      if (String(event?.detail?.meta?.action) === "attendance_confirmed") {
        refreshAfterAttendance(event);
      }
    };

    window.addEventListener("vg:event-attendance-confirmed", refreshAfterAttendance);
    window.addEventListener("vg:notification", refreshAfterNotification);
    return () => {
      window.removeEventListener("vg:event-attendance-confirmed", refreshAfterAttendance);
      window.removeEventListener("vg:notification", refreshAfterNotification);
    };
  }, [chatKind, chatMeta?.buddy_state?.event?.id, refreshBuddyState]);

  useEffect(() => {
    if (chatKind !== "group" || !internalChatId) return;
    const params = new URLSearchParams(location?.search || "");
    const token = String(params.get("buddy_meet") || "").trim();
    if (!token || buddyMeetTokenRef.current === token) return;
    buddyMeetTokenRef.current = token;

    const confirmMeetup = async () => {
      try {
        const response = await api.post(
          `/chats/buddy-meet/${encodeURIComponent(token)}/confirm/`
        );
        const nextState = response?.data?.buddy_state || null;
        if (nextState) {
          setChatMeta((prev) => ({ ...prev, buddy_state: nextState }));
        }
        window.dispatchEvent(new CustomEvent("global-info", {
          detail: {
            message: t("welcome_crew.meetup_success", {
              defaultValue: "Meetup confirmed. Collect your reward in the mission panel.",
            }),
          },
        }));
      } catch (error) {
        const message = error?.response?.data?.error || t("welcome_crew.meetup_failed", {
          defaultValue: "Could not confirm this meetup QR.",
        });
        window.dispatchEvent(new CustomEvent("global-error", { detail: { message } }));
      } finally {
        const nextParams = new URLSearchParams(location?.search || "");
        nextParams.delete("buddy_meet");
        const nextSearch = nextParams.toString();
        navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`, { replace: true });
      }
    };

    void confirmMeetup();
  }, [chatKind, internalChatId, location.pathname, location.search, navigate, t]);

  const handleRealtimeEvent = useCallback(
    (payload) => {
      const event = String(payload?.event || "").toLowerCase();
      if (!event) return;

      if (event === "typing") {
        upsertTypingUser(payload?.user, !!payload?.is_typing);
        return;
      }

      if (event === "buddy.state.changed") {
        void refreshBuddyState();
        return;
      }

      if (event === "message.pinned") {
        setChatMeta((prev) => applyChatPinnedMessages(prev, payload));
        return;
      }

      if (event === "message.deleted") {
        const messageId = Number(payload?.message_id || 0);
        if (!messageId) return;
        setItems((prev) => prev.filter((m) => Number(m?.id || 0) !== messageId));
        setChatMeta((prev) => removeChatPinnedMessage(prev, messageId));
        return;
      }

      if (event !== "message.created" && event !== "message.updated") {
        return;
      }

      const m = payload?.message || {};
      const messageId = Number(m?.id || 0);
      if (!messageId) return;

      const authorId = Number(m?.author_id || 0);
      const viewerUserId = currentUserIdRef.current;
      const isSelf =
        typeof m?.is_self === "boolean"
          ? !!m.is_self
          : viewerUserId != null && authorId > 0 && authorId === Number(viewerUserId);

      let nextItem = null;
      if (m?.message_type === "system") {
        const localizedText = getViewerLocalizedSystemText(m, t);
        nextItem = {
          type: ITEM_TYPES.SYSTEM,
          id: messageId,
          text: localizedText,
          payload: m?.payload || null,
          lines: [localizedText],
        };
      } else if (m?.message_type === "system_as_user") {
        nextItem = {
          type: ITEM_TYPES.MESSAGE,
          id: messageId,
          created_at: m?.created_at || null,
          author_id: null,
          message_type: "system_as_user",
          text: m?.text || "",
          payload: m?.payload || null,
          time: formatShortTime(m?.created_at),
          role: SYSTEM_USER_ROLE,
          is_self: false,
          sender_username: SYSTEM_USER_USERNAME,
          sender_code: SYSTEM_USER_CODE,
          edited_at: m?.edited_at || null,
          reply_to_id: null,
          reply_to: null,
          attention_request: m?.attention_request || null,
          attachments: [],
          reactions: normalizeMessageReactions(m?.reactions, viewerUserId),
        };
      } else {
        nextItem = {
          type: ITEM_TYPES.MESSAGE,
          id: messageId,
          client_id: m?.client_id || null,
          created_at: m?.created_at || null,
          author_id: authorId || null,
          message_type: m?.message_type || "text",
          text: m?.text || "",
          payload: m?.payload || null,
          time: formatShortTime(m?.created_at),
          role: m?.author_role || null,
          author_is_staff: !!m?.author_is_staff,
          author_is_deleted_account: !!m?.author_is_deleted_account,
          is_self: isSelf,
          sender_username: m?.author_username || "@user",
          sender_code: m?.author_code || null,
          avatarSrc: extractAvatarFromMessage(m),
          sender_has_premium_subscription: !!m?.author_has_premium_subscription,
          edited_at: m?.edited_at || null,
          reply_to_id: m?.reply_to_id || null,
          reply_to: m?.reply_to || null,
          attention_request: m?.attention_request || null,
          attachments: normalizeWsAttachments(m?.attachments),
          reactions: normalizeMessageReactions(m?.reactions, viewerUserId),
        };
      }

      setChatMeta((prev) => ({
        ...prev,
        last_message_id: Math.max(Number(prev?.last_message_id || 0), messageId),
      }));

      if (event === "message.updated") {
        setItems((prev) =>
          prev.map((item) => (Number(item?.id || 0) === messageId ? { ...item, ...nextItem } : item))
        );
      } else {
        setItems((prev) => {
          const optimisticClientId = findMatchingOptimisticClientId(prev, nextItem);
          if (optimisticClientId) {
            return reconcileOptimisticChatMessage(prev, optimisticClientId, nextItem);
          }
          const exists = prev.some((item) => Number(item?.id || 0) === messageId);
          if (exists) return prev;
          return [...prev, nextItem];
        });
      }

      let avatarCandidate = null;
      if (nextItem?.sender_code) {
        avatarCandidate =
          (m?.author_avatar_custom_url ? toApiAbsoluteUrl(m.author_avatar_custom_url) : null) ||
          (m?.author_avatar_url ? toApiAbsoluteUrl(m.author_avatar_url) : null) ||
          (m?.author_avatar ? `/assets/avatars/${m.author_avatar}.webp` : null);
      }
      if (nextItem?.sender_code && avatarCandidate) {
        setAvatarByCode((prev) => ({
          ...prev,
          [nextItem.sender_code]: avatarCandidate,
          [SYSTEM_USER_CODE]: SYSTEM_USER_AVATAR_SRC,
          [SUPPORT_USER_CODE]: SUPPORT_USER_AVATAR_SRC,
        }));
      }
    },
    [refreshBuddyState, t, upsertTypingUser]
  );

  useEffect(() => {
    if (!messagesBaseUrl) return undefined;

    const openChat = chatKind === "event"
      ? { type: "event", eventId }
      : { type: chatKind, chatId: internalChatId };
    const syncFromInbox = (event) => {
      const payload = event?.detail || {};
      if (!isInboxEventForOpenChat(payload, openChat)) return;

      if (payload?.message?.id) {
        handleRealtimeEvent({ event: "message.created", message: payload.message });
      }
      requestLatestSync();
    };
    const syncFromNativeState = (event) => {
      if (event?.detail?.isActive !== false) requestLatestSync();
    };
    const syncWhenVisible = () => {
      if (document.visibilityState !== "hidden") requestLatestSync();
    };

    window.addEventListener("vg:chat-inbox-updated", syncFromInbox);
    window.addEventListener("vg:native-app-state", syncFromNativeState);
    window.addEventListener("focus", requestLatestSync);
    document.addEventListener("visibilitychange", syncWhenVisible);
    return () => {
      window.removeEventListener("vg:chat-inbox-updated", syncFromInbox);
      window.removeEventListener("vg:native-app-state", syncFromNativeState);
      window.removeEventListener("focus", requestLatestSync);
      document.removeEventListener("visibilitychange", syncWhenVisible);
    };
  }, [
    chatKind,
    eventId,
    handleRealtimeEvent,
    internalChatId,
    messagesBaseUrl,
    requestLatestSync,
  ]);

  const sendTypingState = useCallback((isTyping) => {
    const ws = chatWsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    const nextState = !!isTyping;
    const now = Date.now();
    if (nextState) {
      if (typingStateRef.current && now - typingLastSentRef.current < 2000) {
        return;
      }
      typingStateRef.current = true;
      typingLastSentRef.current = now;
    } else {
      if (!typingStateRef.current) return;
      typingStateRef.current = false;
      typingLastSentRef.current = now;
    }

    try {
      ws.send(JSON.stringify({ action: "typing", is_typing: nextState }));
    } catch {
      // ignore
    }
  }, []);

  const scheduleTypingStop = useCallback(() => {
    if (typingStopTimerRef.current) {
      try {
        window.clearTimeout(typingStopTimerRef.current);
      } catch {
        // ignore
      }
    }
    typingStopTimerRef.current = window.setTimeout(() => {
      typingStopTimerRef.current = null;
      sendTypingState(false);
    }, 2800);
  }, [sendTypingState]);

  useEffect(() => {
    if (!wsPath) return undefined;

    let disposed = false;

    const clearReconnect = () => {
      if (wsReconnectTimerRef.current) {
        window.clearTimeout(wsReconnectTimerRef.current);
      }
      wsReconnectTimerRef.current = null;
    };

    const closeSocket = () => {
      const ws = chatWsRef.current;
      chatWsRef.current = null;
      if (!ws) return;
      try {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        ws.close();
      } catch {
        // ignore
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      if (wsReconnectTimerRef.current) return;
      const attempt = wsReconnectAttemptRef.current || 0;
      const delayMs = Math.min(20_000, 1000 * (2 ** attempt));
      wsReconnectTimerRef.current = window.setTimeout(() => {
        wsReconnectTimerRef.current = null;
        connect();
      }, delayMs);
      wsReconnectAttemptRef.current = attempt + 1;
    };

    const connect = () => {
      if (disposed) return;
      clearReconnect();
      closeSocket();

      let ws;
      try {
        ws = new WebSocket(buildWsUrl(wsPath));
      } catch {
        scheduleReconnect();
        return;
      }

      chatWsRef.current = ws;
      ws.onopen = () => {
        wsReconnectAttemptRef.current = 0;
        requestLatestSync();
      };
      ws.onmessage = (evt) => {
        try {
          const payload = JSON.parse(evt?.data || "{}");
          handleRealtimeEvent(payload);
        } catch {
          // ignore malformed payload
        }
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          // ignore
        }
      };
      ws.onclose = () => {
        if (disposed) return;
        scheduleReconnect();
      };
    };

    connect();

    return () => {
      disposed = true;
      clearReconnect();
      sendTypingState(false);
      closeSocket();
      clearTypingUsers();
      if (typingStopTimerRef.current) {
        try {
          window.clearTimeout(typingStopTimerRef.current);
        } catch {
          // ignore
        }
        typingStopTimerRef.current = null;
      }
      typingStateRef.current = false;
    };
  }, [
    wsPath,
    handleRealtimeEvent,
    requestLatestSync,
    sendTypingState,
    clearTypingUsers,
  ]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    persistChatDraft(value);
    if (String(value || "").trim()) {
      sendTypingState(true);
      scheduleTypingStop();
    } else {
      if (typingStopTimerRef.current) {
        try {
          window.clearTimeout(typingStopTimerRef.current);
        } catch {
          // ignore
        }
        typingStopTimerRef.current = null;
      }
      sendTypingState(false);
    }
    // Авто-ресайз теперь полностью делает ChatInputBar через свой useEffect
  };

  useEffect(() => {
    if (!messagesBaseUrl) return;

    let cancelled = false;
    const controller = new AbortController();

    const settleInitialPosition = (nextMeta, { empty = false } = {}) => {
      if (cancelled || didInitialScrollRef.current) return;
      initialPositionCleanupRef.current?.();
      initialPositionCleanupRef.current = stabilizeChatInitialPosition({
        feedOrRef: feedRef,
        meta: nextMeta,
        focusFirstUnread: focusFirstUnreadFromPush,
        targetMessageId: requestedMessageId,
        empty,
        onPendingMessage: (messageId) => {
          pendingJumpTargetRef.current = messageId;
          pendingJumpShouldFlashRef.current = Boolean(requestedMessageId);
        },
        onSettled: () => {
          if (cancelled) return;
          didInitialScrollRef.current = true;
          setInitialPositionSettled(true);
        },
      });
    };

    async function loadMessages() {
      try {
        const resp = await loadChatMessagePage(messagesBaseUrl, PAGE_LIMIT, {
          signal: controller.signal,
        });
        if (cancelled) return;

        const serverMessages = resp?.data?.messages || [];
        const isRecoverySync = latestLoadedBaseUrlRef.current === messagesBaseUrl;
        latestLoadedBaseUrlRef.current = messagesBaseUrl;

        const meta = resp?.data?.meta || {};
        const serverMeta = {
          ...applyChatPinnedMessages({}, meta),
          last_read_message_id: Number(meta?.last_read_message_id || 0),
          unread_count: Number(meta?.unread_count || 0),
          first_unread_message_id: Number(meta?.first_unread_message_id || 0),
          last_message_id: Number(meta?.last_message_id || 0),
          can_moderate_messages: Boolean(meta?.can_moderate_messages),
          can_send_messages: Boolean(meta?.can_send_messages ?? true),
          send_disabled_reason: meta?.send_disabled_reason || "",
          can_request_message_attention: Boolean(meta?.can_request_message_attention),
          message_attention_requests_used: Number(meta?.message_attention_requests_used || 0),
          message_attention_requests_limit: Number(
            meta?.message_attention_requests_limit || DEFAULT_MESSAGE_ATTENTION_LIMIT
          ),
          assistant_state: meta?.assistant_state || chat?.assistant_state || null,
          staff_moderation_assessment: meta?.staff_moderation_assessment || null,
          club: meta?.club || chat?.club || null,
          new_joiners_event_cta: meta?.new_joiners_event_cta || chat?.new_joiners_event_cta || null,
          buddy_state: meta?.buddy_state || chat?.buddy_state || null,
          notifications_muted: Boolean(meta?.notifications_muted),
          support: meta?.support || null,
        };
        const nextMeta = reconcileChatReadPosition(messagesBaseUrl, serverMeta);
        const firstUnreadTarget = requestedMessageId || resolveFirstUnreadMessageId(
          nextMeta,
          focusFirstUnreadFromPush || Number(nextMeta?.unread_count || 0) > 0
        );
        if (firstUnreadTarget) {
          pendingJumpTargetRef.current = firstUnreadTarget;
          pendingJumpAttemptsRef.current = 0;
          pendingJumpShouldFlashRef.current = Boolean(requestedMessageId);
        }
        setChatMeta(nextMeta);
        setHasOlder(!!meta?.has_older);

        // Opening a chat acknowledges the complete snapshot returned by the backend.
        // Use the server snapshot here so a locally remembered acknowledgement
        // is retried after a transient failed request instead of being stranded.
        const openReadTarget = resolveChatOpenReadTarget(serverMeta);
        if (openReadTarget > 0) void postReadUpTo(openReadTarget);

        if (!serverMessages.length) {
          if (!isRecoverySync) setItems([]);
          setInitialMessagesSettled(true);
          settleInitialPosition(nextMeta, { empty: true });
          return;
        }

        const mapped = serverMessages.map((m) => {
          if (m.message_type === "system") {
            const localizedText = getViewerLocalizedSystemText(m, t);
            return {
              type: ITEM_TYPES.SYSTEM,
              id: m.id,
              text: localizedText,
              payload: m.payload || null,
              lines: [localizedText],
            };
          }

          // Special: system message rendered as a normal bubble from a virtual user (VG)
          if (m.message_type === "system_as_user") {
            return {
              type: ITEM_TYPES.MESSAGE,
              id: m.id,
              created_at: m.created_at || null,
              author_id: null,
              message_type: "system_as_user",
              text: m.text,
              payload: m.payload || null,
              time: formatShortTime(m.created_at),
              role: SYSTEM_USER_ROLE,
              is_self: false,
              sender_username: SYSTEM_USER_USERNAME,
              sender_code: SYSTEM_USER_CODE,
              edited_at: m.edited_at || null,
              reply_to_id: null,
              reply_to: null,
              attention_request: m.attention_request || null,
              attachments: [],
              reactions: normalizeMessageReactions(m.reactions, currentUserIdRef.current),
            };
          }

          const rawAtt = Array.isArray(m.attachments) ? m.attachments : [];
          const attachments = rawAtt
            .map((a) => {
              const t = a?.type || a?.attachment_type || null;
              const url = a?.url || a?.file_url || a?.file || a?.external_url || null;
              if (!t || !url) return null;
              return {
                type: t,
                url: toApiAbsoluteUrl(url) || url,
                width: a?.width ?? null,
                height: a?.height ?? null,
                original_name: a?.original_name ?? null,
                size_bytes: a?.size_bytes ?? null,
              };
            })
            .filter(Boolean);

          return {
            type: ITEM_TYPES.MESSAGE,
            id: m.id,
            created_at: m.created_at || null,
            author_id: m.author_id ?? m.author?.id ?? null,
            message_type: m.message_type || "text",
            text: m.text,
            payload: m.payload || null,
            time: formatShortTime(m.created_at),
            role: m.author_role || null,
            author_is_staff: !!m.author_is_staff,
            author_is_deleted_account: !!m.author_is_deleted_account,
            is_self: !!m.is_self,
            sender_username: m.author_username || "@user",
            sender_code: m.author_code || null,
            avatarSrc: extractAvatarFromMessage(m),
            sender_has_premium_subscription: !!m.author_has_premium_subscription,
            edited_at: m.edited_at || null,
            reply_to_id: m.reply_to_id || null,
            reply_to: m.reply_to || null,
            attention_request: m.attention_request || null,
            attachments,
            reactions: normalizeMessageReactions(m.reactions, currentUserIdRef.current),
          };
        });

        // Обновляем карту аватаров из авторов сообщений
        const avatarMapFromMessages = {};
        mapped.forEach((item, idx) => {
          if (item.type !== ITEM_TYPES.MESSAGE) return;

          const src = extractAvatarFromMessage(serverMessages[idx]);
          const code = item.sender_code;
          if (code && src) {
            avatarMapFromMessages[code] = src;
          }
        });

        if (Object.keys(avatarMapFromMessages).length) {
          setAvatarByCode((prev) => ({
            ...prev,
            ...avatarMapFromMessages,
            [SYSTEM_USER_CODE]: SYSTEM_USER_AVATAR_SRC,
            [SUPPORT_USER_CODE]: SUPPORT_USER_AVATAR_SRC,
          }));
        }

        setItems((prev) =>
          isRecoverySync ? mergeLatestChatPage(prev, mapped) : mapped
        );
        setInitialMessagesSettled(true);
        settleInitialPosition(nextMeta);
      } catch (err) {
        // eslint-disable-next-line no-console
        if (cancelled) return;
        console.error("Failed to load chat messages", err);
        if (!cancelled) {
          if (onInitialError && !initialReadyNotifiedRef.current) {
            onInitialError();
          } else if (!onInitialError) {
            setInitialMessagesSettled(true);
            setInitialPositionSettled(true);
          }
        }
      }
    }

    loadMessages();

    return () => {
      cancelled = true;
      controller.abort();
      // A realtime/focus refresh reruns this request effect. Keep the initial
      // position observer alive across those refreshes; the chat-identity
      // effect above owns its lifecycle and stops it only on a real reopen,
      // chat switch, requested-message change, or unmount.
    };
  }, [
    chat?.assistant_state,
    chat?.club,
    chat?.new_joiners_event_cta,
    chat?.buddy_state,
    focusFirstUnreadFromPush,
    latestSyncVersion,
    messagesBaseUrl,
    onInitialError,
    postReadUpTo,
    pushUnreadFocusKey,
    requestedMessageId,
    t,
  ]);

  useEffect(() => {
    const feed = feedRef.current;
    if (!feed) return;

    const isNearBottom = () => {
      try {
        return feed.scrollHeight - (feed.scrollTop + feed.clientHeight) < 120;
      } catch {
        return false;
      }
    };

    // Auto-scroll only if:
    // 1) I just sent a message, OR
    // 2) user was already near the bottom
    const shouldAutoScroll = justSentRef.current || isNearBottom();

    if (shouldAutoScroll) {
      scheduleChatFeedScrollToBottom(feedRef);
    }

    justSentRef.current = false;
  }, [items.length]);

  const extractSendErrorMessage = React.useCallback((err, fallbackMsg) => {
    const status = err?.response?.status;
    const code = String(err?.code || "").trim();
    if (status === 413) {
      return t("chats.errors.file_too_large", { max_mb: CHAT_MAX_FILE_MB });
    }
    if (code === "ECONNABORTED") {
      return "Upload is taking too long. Please try again.";
    }
    if (code === "IMAGE_PREP_FAILED") {
      return "Could not prepare this iPhone photo for upload. Please try another photo or send it as JPEG.";
    }
    if (code === "ERR_NETWORK" || !err?.response) {
      return "Upload was blocked before the server responded. The image may still be too large for the upload proxy.";
    }

    const responseData = err?.response?.data;
    const nestedMessage =
      responseData?.error ||
      responseData?.detail ||
      responseData?.message ||
      null;

    if (typeof nestedMessage === "string" && nestedMessage.trim()) {
      return nestedMessage.trim();
    }

    if (typeof responseData === "string") {
      const plain = responseData
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      if (plain && !/request\s+entity\s+too\s+large|nginx/i.test(plain)) {
        return plain.slice(0, 220);
      }
    }

    return fallbackMsg;
  }, [t]);

  const postMultipartMessage = React.useCallback(async (url, formData, uploadFilesMeta = []) => {
    const cookieMatch = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
    const csrfToken = cookieMatch?.[1] ? decodeURIComponent(cookieMatch[1]) : "";
    const uiLangRaw = String(localStorage.getItem("vg_ui_language") || document?.documentElement?.lang || "")
      .trim()
      .toLowerCase();
    const uiLang = uiLangRaw.includes("-") ? uiLangRaw.split("-")[0] : uiLangRaw;

    const headers = {};
    if (csrfToken) headers["X-CSRFToken"] = csrfToken;
    if (["en", "ru", "sk"].includes(uiLang)) headers["X-UI-Language"] = uiLang;

    try {
      // Use the shared API client so native uploads receive the same Bearer
      // token as every other protected request. A raw fetch only carries web
      // cookies and therefore returns 403 inside Capacitor.
      return await api.post(url, formData, {
        headers,
        timeout: 65000,
        meta: { suppressGlobalError: true },
      });
    } catch (error) {
      error.uploadFilesMeta = uploadFilesMeta;
      throw error;
    }
  }, []);

  const handleSend = async (options = {}) => {
    const stickerId = getChatSticker(options?.stickerId)?.id || null;
    const isStickerSend = Boolean(stickerId);
    const rawDraft = String(inputRef.current?.value ?? inputValue ?? "");
    const text = isStickerSend ? "" : rawDraft.trim();
    const hasImages = !isStickerSend && pendingImages.length > 0;
    const pendingImageIdsForSend = isStickerSend
      ? []
      : pendingImages.map((image) => image?.id).filter(Boolean);
    if (
      (!text && !hasImages && !isStickerSend) ||
      !messagesBaseUrl ||
      sending ||
      sendInFlightRef.current ||
      !canSendMessages
    ) return;

    const hasTooLargeImage = !isStickerSend && pendingImages.some(
      (p) => Number(p?.size || 0) > CHAT_MAX_FILE_BYTES
    );
    if (hasTooLargeImage) {
      try {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: {
              status: 400,
              message: t("chats.errors.file_too_large", { max_mb: CHAT_MAX_FILE_MB }),
            },
          })
        );
      } catch {
        // ignore
      }
      return;
    }

    sendInFlightRef.current = true;

    const isEditing = !!editingId;
    const draftBeforeSend = rawDraft;
    let draftClearedForSend = false;
    let sendSucceeded = false;
    let optimisticClientId = null;

    try {
      if (typingStopTimerRef.current) {
        try {
          window.clearTimeout(typingStopTimerRef.current);
        } catch {
          // ignore
        }
        typingStopTimerRef.current = null;
      }
      sendTypingState(false);
      setSending(true);

      if (!isEditing && !isStickerSend) {
        draftClearedForSend = true;
        clearChatDraft();
        if (inputRef.current) {
          inputRef.current.style.height = "";
          inputRef.current.style.overflowY = "auto";
        }
      }

      if (!isEditing) {
        const optimisticPayload = isStickerSend
          ? { kind: "sticker", sticker_id: stickerId }
          : null;
        const optimisticItem = buildOptimisticChatMessage({
          text,
          payload: optimisticPayload,
          replyTo,
          pendingImages: isStickerSend ? [] : pendingImages.slice(0, MAX_ATTACHMENTS),
          authorId: currentUserId,
          role: myRoleDetected,
          formatTime: formatShortTime,
        });
        optimisticClientId = optimisticItem.client_id;
        justSentRef.current = true;
        setItems((prev) => [...prev, optimisticItem]);
        scheduleChatFeedScrollToBottom(feedRef);
      }

      if (isEditing) {
        try {
          const formData = new FormData();
          formData.append("text", text);
          const resp = await api.post(`/chats/internal-chat/messages/${editingId}/edit/`, formData);
          const payload = resp?.data?.message;

          if (payload) {
            setItems((prev) =>
              prev.map((m) =>
                m?.id === editingId
                  ? {
                      ...m,
                      text: payload.text,
                      time: formatShortTime(payload.created_at || m.created_at),
                      edited_at: payload.edited_at || new Date().toISOString(),
                      reactions:
                        payload.reactions != null
                          ? normalizeMessageReactions(payload.reactions, currentUserId)
                          : m.reactions || [],
                    }
                  : m
              )
            );
          } else {
            // fallback: optimistic local update
            setItems((prev) =>
              prev.map((m) =>
                m?.id === editingId
                  ? { ...m, text, edited_at: new Date().toISOString() }
                  : m
              )
            );
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error("Edit endpoint not available yet (or failed)", err);
          window.dispatchEvent(
            new CustomEvent("global-error", {
              detail: {
                message:
                  err?.response?.data?.error ||
                  t("chats.event_chat.ctx.edit_failed", {
                    defaultValue: "Could not edit this message.",
                  }),
                status: err?.response?.status,
              },
            })
          );
          return;
        }

        setEditingId(null);
        setInputValue("");
        setEditingMeta(null);
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.style.height = "";
            inputRef.current.style.overflowY = "auto";
          }
        });
        return;
      }

      const formData = new FormData();
      // Backend should accept empty text when images are present
      formData.append("text", text || "");
      if (isStickerSend) {
        formData.append("payload", JSON.stringify({ kind: "sticker", sticker_id: stickerId }));
      }
      if (optimisticClientId) formData.append("client_id", optimisticClientId);
      if (replyTo?.id) formData.append("reply_to", String(replyTo.id));
      if (isSupportChat && sendWithPersonalPhoto) formData.append("support_avatar", "personal");

      // images only (MVP). Backend expects multi-part files; use key `files`.
      const uploadFilesMeta = [];
      if (!isStickerSend && pendingImages.length) {
        const filesForUpload = [];
        try {
          for (const p of pendingImages.slice(0, MAX_ATTACHMENTS)) {
            if (!p?.file) continue;
            const fileToSend = await _prepareChatImageForUpload(p.file);
            uploadFilesMeta.push({
              originalName: p.file?.name || null,
              originalType: p.file?.type || null,
              originalSize: p.file?.size || 0,
              uploadName: fileToSend?.name || null,
              uploadType: fileToSend?.type || null,
              uploadSize: fileToSend?.size || 0,
            });
            filesForUpload.push(fileToSend);
          }
        } catch (err) {
          try {
            window.dispatchEvent(
              new CustomEvent("global-error", {
                detail: {
                  status: 400,
                  message: extractSendErrorMessage(err, "Could not prepare image for upload."),
                },
              })
            );
          } catch {
            // ignore
          }

          // eslint-disable-next-line no-console
          console.error("Failed to prepare chat image for upload", {
            err,
            files: pendingImages.map((p) => ({
              name: p?.file?.name || null,
              type: p?.file?.type || null,
              size: p?.file?.size || 0,
            })),
          });
          return;
        }
        filesForUpload.forEach((f) => formData.append("files", f));
      }

      let resp = null;
      // Set this before the request: the websocket can deliver our own message
      // before the HTTP response is handled and deduplicated.
      justSentRef.current = true;
      try {
        if (hasImages) {
          // Keep this API-relative: the shared client adds /api on web and the
          // native API origin plus Bearer auth in Capacitor.
          resp = await postMultipartMessage(
            `${messagesBaseUrl}/messages/send/`,
            formData,
            uploadFilesMeta
          );
        } else {
          resp = await api.post(`${messagesBaseUrl}/messages/send/`, formData, {
            timeout: 65000,
          });
        }
        sendSucceeded = true;
        if (isSupportChat) setSendWithPersonalPhoto(false);
      } catch (err) {
        justSentRef.current = false;
        const status = err?.response?.status;
        const fallbackMsg =
          status === 413
            ? t("chats.errors.file_too_large", { max_mb: CHAT_MAX_FILE_MB })
            : err?.code === "ECONNABORTED"
              ? "Upload is taking too long. Please try again."
              : "Message was not sent. Please try again.";
        const message = extractSendErrorMessage(err, fallbackMsg);

        try {
          window.dispatchEvent(
            new CustomEvent("global-error", {
              detail: {
                status: status ?? 0,
                message,
              },
            })
          );
        } catch {
          // ignore
        }

        // eslint-disable-next-line no-console
        console.error("Failed to send chat message", {
          err,
          status,
          uploadFilesMeta: err?.uploadFilesMeta || uploadFilesMeta,
        });
        return;
      }

      try {
        const payload = resp?.data?.message;
        const buddyState = resp?.data?.buddy_state || null;
        if (buddyState) {
          setChatMeta((prev) => ({ ...prev, buddy_state: buddyState }));
        }

        if (payload) {
          const rawAtt = Array.isArray(payload.attachments) ? payload.attachments : [];
          const attachments = rawAtt
            .map((a) => {
              const at = a?.type || a?.attachment_type || null;
              const url = a?.url || a?.file_url || a?.file || a?.external_url || null;
              if (!at || !url) return null;
              return {
                type: at,
                url: toApiAbsoluteUrl(url) || url,
                width: a?.width ?? null,
                height: a?.height ?? null,
                original_name: a?.original_name ?? null,
                size_bytes: a?.size_bytes ?? null,
              };
            })
            .filter(Boolean);

          const newItem = {
            type: ITEM_TYPES.MESSAGE,
            id: payload.id,
            created_at: payload.created_at || new Date().toISOString(),
            author_id: payload.author_id ?? payload.author?.id ?? null,
            message_type: payload.message_type || "text",
            text: payload.text,
            payload: payload.payload || null,
            time: formatShortTime(payload.created_at),
            role: payload.author_role || null,
            author_is_staff: !!payload.author_is_staff,
            author_is_deleted_account: !!payload.author_is_deleted_account,
            is_self: true,
            sender_username: payload.author_username || "",
            sender_code: payload.author_code || null,
            avatarSrc: extractAvatarFromMessage(payload),
            sender_has_premium_subscription: !!payload.author_has_premium_subscription,
            reply_to_id: replyTo?.id ? String(replyTo.id) : null,
            reply_to: replyTo
              ? { id: replyTo.id, text: replyTo.preview, author_username: replyTo.author }
              : null,
            attachments,
            reactions: normalizeMessageReactions(payload.reactions, currentUserId),
          };
          setItems((prev) => {
            if (optimisticClientId) {
              return reconcileOptimisticChatMessage(prev, optimisticClientId, newItem);
            }
            const messageId = Number(newItem?.id || 0);
            if (!messageId) return [...prev, newItem];

            const existingIdx = prev.findIndex(
              (item) => Number(item?.id || 0) === messageId
            );
            if (existingIdx < 0) return [...prev, newItem];

            const next = [...prev];
            next[existingIdx] = { ...next[existingIdx], ...newItem };
            return next;
          });

          // Update meta last_message_id so the unread bubble jump-to-bottom stays correct
          setChatMeta((prev) => ({
            ...prev,
            last_message_id: Math.max(Number(prev.last_message_id || 0), Number(payload.id || 0)),
          }));

          const botReplyPayload = resp?.data?.bot_reply || null;
          if (botReplyPayload?.id) {
            const botReplyItem = {
              type: ITEM_TYPES.MESSAGE,
              id: botReplyPayload.id,
              created_at: botReplyPayload.created_at || null,
              author_id: botReplyPayload.author_id ?? botReplyPayload.author?.id ?? null,
              message_type: botReplyPayload.message_type || "text",
              text: botReplyPayload.text,
              payload: botReplyPayload.payload || null,
              time: formatShortTime(botReplyPayload.created_at),
              role: botReplyPayload.author_role || null,
              author_is_staff: !!botReplyPayload.author_is_staff,
              author_is_deleted_account: !!botReplyPayload.author_is_deleted_account,
              is_self: false,
              sender_username: botReplyPayload.author_username || "@user",
              sender_code: botReplyPayload.author_code || null,
              sender_has_premium_subscription: !!botReplyPayload.author_has_premium_subscription,
              edited_at: botReplyPayload.edited_at || null,
              reply_to_id: botReplyPayload.reply_to_id || null,
              reply_to: botReplyPayload.reply_to || null,
              attachments: normalizeWsAttachments(botReplyPayload.attachments),
              reactions: normalizeMessageReactions(botReplyPayload.reactions, currentUserId),
            };

            setItems((prev) => {
              const messageId = Number(botReplyItem?.id || 0);
              if (!messageId) return prev;
              if (prev.some((item) => Number(item?.id || 0) === messageId)) {
                return prev;
              }
              return [...prev, botReplyItem];
            });

            const botAvatar =
              (botReplyPayload.author_avatar_custom_url
                ? toApiAbsoluteUrl(botReplyPayload.author_avatar_custom_url)
                : null) ||
              (botReplyPayload.author_avatar_url
                ? toApiAbsoluteUrl(botReplyPayload.author_avatar_url)
                : null) ||
              (botReplyPayload.author_avatar
                ? `/assets/avatars/${botReplyPayload.author_avatar}.webp`
                : null);
            if (botReplyItem.sender_code && botAvatar) {
              setAvatarByCode((prev) => ({
                ...prev,
                [botReplyItem.sender_code]: botAvatar,
                [SYSTEM_USER_CODE]: SYSTEM_USER_AVATAR_SRC,
                [SUPPORT_USER_CODE]: SUPPORT_USER_AVATAR_SRC,
              }));
            }

            setChatMeta((prev) => ({
              ...prev,
              last_message_id: Math.max(
                Number(prev.last_message_id || 0),
                Number(botReplyPayload.id || 0)
              ),
            }));
          }

          // When I send a message, keep the view pinned to bottom
          justSentRef.current = true;
        }
      } catch (parseErr) {
        // eslint-disable-next-line no-console
        console.error("Chat send succeeded, but response handling failed", parseErr, resp?.data);
      }

      setReplyTo(null);
      if (!isStickerSend) {
        setInputValue("");
        clearChatDraft();
        // Only remove the images captured by this send. If the user opened the
        // picker while the request was in flight, keep that new selection for
        // the next message.
        clearPendingImages(pendingImageIdsForSend);
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.style.height = "";
            inputRef.current.style.overflowY = "auto";
          }
        });
      }
    } finally {
      if (draftClearedForSend && !sendSucceeded) {
        restoreChatDraft(draftBeforeSend);
      }
      if (optimisticClientId && !sendSucceeded) {
        setItems((prev) => removeOptimisticChatMessage(prev, optimisticClientId));
      }
      if (sendSucceeded) {
        scheduleChatFeedScrollToBottom(feedRef);
      }
      sendInFlightRef.current = false;
      setSending(false);
    }
  };

  const sendStructuredMessage = async ({
    text = "",
    caption = "",
    payload: structuredPayload = null,
  } = {}) => {
    const cleanText = String(text || "").trim();
    const cleanCaption = String(caption || "").trim();
    if (!structuredPayload || !messagesBaseUrl || sending || !canSendMessages) return false;

    const structuredKind = String(structuredPayload?.kind || "").trim().toLowerCase();
    const isChatPollPayload = structuredKind === "chat_poll";
    const payloadToSend = isChatPollPayload
      ? (() => {
          const nextPayload = { ...structuredPayload };
          delete nextPayload.caption;
          return nextPayload;
        })()
      : structuredPayload;
    let structuredSendSucceeded = false;

    const appendSentMessage = (messagePayload, fallbackText, fallbackPayload, replyContext) => {
      if (!messagePayload) return;

      const newItem = {
        type: ITEM_TYPES.MESSAGE,
        id: messagePayload.id,
        created_at: messagePayload.created_at || new Date().toISOString(),
        author_id: messagePayload.author_id ?? messagePayload.author?.id ?? null,
        message_type: messagePayload.message_type || "text",
        text: messagePayload.text || fallbackText,
        payload: messagePayload.payload || fallbackPayload || null,
        time: formatShortTime(messagePayload.created_at),
        role: messagePayload.author_role || null,
        author_is_staff: !!messagePayload.author_is_staff,
        author_is_deleted_account: !!messagePayload.author_is_deleted_account,
        is_self: true,
        sender_username: messagePayload.author_username || "",
        sender_code: messagePayload.author_code || null,
        avatarSrc: extractAvatarFromMessage(messagePayload),
        sender_has_premium_subscription: !!messagePayload.author_has_premium_subscription,
        reply_to_id: replyContext?.id ? String(replyContext.id) : null,
        reply_to: replyContext
          ? { id: replyContext.id, text: replyContext.preview, author_username: replyContext.author }
          : null,
        attachments: normalizeWsAttachments(messagePayload.attachments),
        reactions: normalizeMessageReactions(messagePayload.reactions, currentUserId),
      };

      setItems((prev) => {
        const messageId = Number(newItem?.id || 0);
        if (!messageId) return [...prev, newItem];
        const existingIdx = prev.findIndex((item) => Number(item?.id || 0) === messageId);
        if (existingIdx < 0) return [...prev, newItem];
        const next = [...prev];
        next[existingIdx] = { ...next[existingIdx], ...newItem };
        return next;
      });

      setChatMeta((prev) => ({
        ...prev,
        last_message_id: Math.max(Number(prev.last_message_id || 0), Number(messagePayload.id || 0)),
      }));
      justSentRef.current = true;
    };

    try {
      if (typingStopTimerRef.current) {
        try {
          window.clearTimeout(typingStopTimerRef.current);
        } catch {
          // ignore
        }
        typingStopTimerRef.current = null;
      }
      sendTypingState(false);
      setSending(true);
      justSentRef.current = true;

      const pollCaptionReplyContext = isChatPollPayload && cleanCaption ? replyTo : null;
      if (pollCaptionReplyContext) {
        const captionFormData = new FormData();
        captionFormData.append("text", cleanCaption);
        captionFormData.append("reply_to", String(pollCaptionReplyContext.id));
        const captionResp = await api.post(`${messagesBaseUrl}/messages/send/`, captionFormData, {
          suppressGlobalError: true,
        });
        structuredSendSucceeded = true;
        appendSentMessage(
          captionResp?.data?.message,
          cleanCaption,
          null,
          pollCaptionReplyContext
        );
      } else if (isChatPollPayload && cleanCaption) {
        const captionFormData = new FormData();
        captionFormData.append("text", cleanCaption);
        const captionResp = await api.post(`${messagesBaseUrl}/messages/send/`, captionFormData, {
          suppressGlobalError: true,
        });
        structuredSendSucceeded = true;
        appendSentMessage(captionResp?.data?.message, cleanCaption, null, null);
      }

      const formData = new FormData();
      formData.append("text", isChatPollPayload ? "" : cleanText);
      formData.append("payload", JSON.stringify(payloadToSend));
      if (replyTo?.id && !(isChatPollPayload && cleanCaption)) {
        formData.append("reply_to", String(replyTo.id));
      }

      const resp = await api.post(`${messagesBaseUrl}/messages/send/`, formData, {
        suppressGlobalError: true,
      });
      structuredSendSucceeded = true;
      const messagePayload = resp?.data?.message;

      appendSentMessage(
        messagePayload,
        isChatPollPayload ? payloadToSend.title : cleanText,
        payloadToSend,
        isChatPollPayload && cleanCaption ? null : replyTo
      );

      setReplyTo(null);
      return true;
    } catch (err) {
      if (!structuredSendSucceeded) {
        justSentRef.current = false;
      }
      const status = err?.response?.status;
      const message = extractSendErrorMessage(
        err,
        "Message was not sent. Please try again."
      );
      try {
        window.dispatchEvent(
          new CustomEvent("global-error", {
            detail: { status: status ?? 0, message },
          })
        );
      } catch {
        // ignore
      }
      return false;
    } finally {
      if (structuredSendSucceeded) {
        scheduleChatFeedScrollToBottom(feedRef);
      }
      setSending(false);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !e.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  const eventSupportsPartySession = Boolean(
    eventForCard?.is_vaultgrid_party || eventForCard?.is_new_joiners_event
  );
  const activePartySessionAction = requiredActions.find(
    (item) =>
      item?.type === "join_party_session"
      && Number(item?.event?.id) === Number(eventId)
  );
  const showJoinSessionCta = Boolean(
    chatKind === "event" &&
      normalizedEventStatus === "active" &&
      eventSupportsPartySession &&
      participantPrimaryAction?.isParticipant &&
      !participantPrimaryAction?.isHost &&
      (isViewerAttendanceConfirmed || participantPrimaryAction?.attended || activePartySessionAction)
  );
  const showConfirmAttendanceCta = Boolean(
    chatKind === "event" &&
      !isConceptEvent &&
      attendanceWindowOpenedByTime &&
      !showJoinSessionCta &&
      !isViewerAttendanceConfirmed &&
      !participantPrimaryAction?.attended &&
      (
        isLocalAttendanceCtaDebug
          ? true
          : participantPrimaryAction?.isParticipant && !participantPrimaryAction?.isHost
      )
  );
  const conceptParticipantCountForActivation = Math.max(
    0,
    Number(participantsCount || 0) - (isHostInEventChat ? 1 : 0)
  );
  const showActivateConceptCta = Boolean(
    chatKind === "event" &&
      isConceptEvent &&
      isHostInEventChat &&
      ["active", "postponed", "pending"].includes(normalizedEventStatus) &&
      conceptActivationMaxParticipants > 0 &&
      conceptParticipantCountForActivation > conceptActivationMaxParticipants / 2
  );
  const showPostEventGalleryCta = Boolean(
    chatKind === "event" &&
      eventId &&
      normalizedEventStatus === "finished" &&
      (isHostInEventChat || participantPrimaryAction?.isHost || participantPrimaryAction?.isParticipant)
  );
  const newJoinersHeaderCta = chatMeta?.new_joiners_event_cta || chat?.new_joiners_event_cta || null;
  const showNewJoinersHeaderCta = Boolean(
    chatKind === "group" &&
      chat?.cohort?.group_kind === "new_joiners" &&
      newJoinersHeaderCta &&
      String(newJoinersHeaderCta?.to || "").trim()
  );
  const newJoinersEventId = extractEventIdFromPath(newJoinersHeaderCta?.to);
  const removalEventId = chatKind === "event" ? eventId : newJoinersEventId;
  const removalParticipantsEndpoint = removalEventId
    ? `/chats/${removalEventId}/internal-chat/participants/`
    : null;
  const openNewJoinersEventFromHeader = React.useCallback(() => {
    const to = String(newJoinersHeaderCta?.to || "").trim();
    if (!to) return;
    navigate(to);
  }, [navigate, newJoinersHeaderCta]);

  const handleParticipantsLoaded = React.useCallback(
    ({ participants = [], participantsCount: nextCount = 0, canKick = false } = {}) => {
      const list = Array.isArray(participants) ? participants : [];
      setChatParticipantsSnapshot(list);
      setChatParticipantsCanRemove(Boolean(canKick));

      if (chatKind === "event" && String(removalEventId || "") === String(eventId || "")) {
        setRemovalParticipants(list);
        setRemovalParticipantsCanRemove(Boolean(canKick));
      }

      if (typeof nextCount === "number" && !Number.isNaN(nextCount)) {
        setParticipantsCount(nextCount);
      }
    },
    [chatKind, eventId, removalEventId]
  );

  const loadRemovalParticipants = React.useCallback(async () => {
    if (!removalParticipantsEndpoint) return [];

    const resp = await api.get(removalParticipantsEndpoint);
    const list = Array.isArray(resp?.data?.participants) ? resp.data.participants : [];
    const resolvedCount = Number(resp?.data?.participants_count);
    const nextCount =
      Number.isFinite(resolvedCount) && resolvedCount >= 0
        ? resolvedCount
        : list.length;

    const me =
      list.find((u) => u?.is_self || u?.is_me || u?.me) ||
      null;
    const myRemovalRole =
      me?.role ||
      me?.member_role ||
      me?.author_role ||
      me?.event_role ||
      me?.chat_role ||
      me?.my_role ||
      null;

    setRemovalParticipants(list);
    setRemovalParticipantsCanRemove(isHostRole(myRemovalRole));

    if (chatKind === "event" && String(removalEventId || "") === String(eventId || "")) {
      setParticipantsCount(nextCount);
      setChatParticipantsSnapshot(list);
      setChatParticipantsCanRemove(isHostRole(myRemovalRole));
    }

    return list;
  }, [chatKind, eventId, removalEventId, removalParticipantsEndpoint]);

  const openRemoveParticipantsModal = React.useCallback(async () => {
    if (!removalEventId || !removalParticipantsEndpoint || removalParticipantsLoading) return;
    if (!isHostInEventChat && !removalParticipantsCanRemove && !chatParticipantsCanRemove) return;

    setRemovalParticipantsLoading(true);
    try {
      await loadRemovalParticipants();
      setRemoveParticipantModalOpen(true);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        te("details.more_actions.remove_participant_modal.generic_error", {
          defaultValue: "Could not remove participants.",
        });
      window.dispatchEvent(
        new CustomEvent("global-error", {
          detail: { message, status: err?.response?.status },
        })
      );
    } finally {
      setRemovalParticipantsLoading(false);
    }
  }, [
    loadRemovalParticipants,
    removalEventId,
    removalParticipantsEndpoint,
    removalParticipantsLoading,
    isHostInEventChat,
    removalParticipantsCanRemove,
    chatParticipantsCanRemove,
    te,
  ]);

  const handleRemoveParticipantsRemoved = React.useCallback(
    async (removedUserIds = []) => {
      const removedSet = new Set(
        (Array.isArray(removedUserIds) ? removedUserIds : [])
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value) && value > 0)
      );
      if (!removedSet.size) return;

      const filterRemoved = (list) =>
        (Array.isArray(list) ? list : []).filter((participant) => {
          const userId = getParticipantUserId(participant);
          return !userId || !removedSet.has(userId);
        });

      setRemovalParticipants((prev) => filterRemoved(prev));
      setChatParticipantsSnapshot((prev) => filterRemoved(prev));

      if (chatKind === "event" && String(removalEventId || "") === String(eventId || "")) {
        setParticipantsCount((prev) => Math.max(0, Number(prev || 0) - removedSet.size));
      }

      setParticipantsReloadKey((prev) => prev + 1);

      try {
        queryClient.invalidateQueries({ queryKey: ["chat_unread_summary"] });
      } catch {
        // ignore
      }
    },
    [chatKind, eventId, queryClient, removalEventId]
  );

  const openPartySessionFromChatHeader = React.useCallback(() => {
    if (!eventId) return;
    const action = activePartySessionAction;
    if (action?.id) {
      dismissRequiredActionForSession?.();
    }
    navigate(action?.payload?.session_path || `/events/${eventId}/party-session`);
  }, [
    activePartySessionAction,
    dismissRequiredActionForSession,
    eventId,
    navigate,
  ]);
  const openPostEventGalleryFromChat = React.useCallback(() => {
    if (!eventId) return;
    navigate(`/events/${eventId}/gallery`);
  }, [eventId, navigate]);
  const showHeaderPrimaryCta = Boolean(
    showActivateConceptCta || showConfirmAttendanceCta || showJoinSessionCta || showPostEventGalleryCta
  );
  const headerPrimaryCtaLabel = showActivateConceptCta
    ? te("events.details.activate_concept")
    : showJoinSessionCta
      ? t("chats.event_chat.join_session_cta")
      : showPostEventGalleryCta
        ? t("chats.event_chat.share_media_cta", { defaultValue: "Share media" })
        : t("chats.event_chat.confirm_attendance_cta");
  const onHeaderPrimaryCtaClick = showActivateConceptCta
    ? openConceptActivationFromChatHeader
    : showJoinSessionCta
      ? openPartySessionFromChatHeader
      : showPostEventGalleryCta
        ? openPostEventGalleryFromChat
        : openConfirmAttendanceFromChat;
  const bottomPrimaryCtaLabel = showHeaderPrimaryCta
    ? headerPrimaryCtaLabel
    : showNewJoinersHeaderCta
      ? (String(newJoinersHeaderCta?.label || "").trim() || t("chats.groups.new_joiners_title"))
      : "";
  const bottomPrimaryCtaAction = showHeaderPrimaryCta
    ? onHeaderPrimaryCtaClick
    : showNewJoinersHeaderCta
      ? openNewJoinersEventFromHeader
      : null;
  const removalEventHostCode = normalizeParticipantCode(
    eventForCard?.host?.code ||
      eventForCard?.created_by?.code ||
      chat?.event?.host?.code ||
      chat?.event?.created_by?.code
  );
  const removalHostParticipant = (Array.isArray(removalParticipants) ? removalParticipants : []).find((participant) => {
    const role =
      participant?.role ||
      participant?.chat_role ||
      participant?.member_role ||
      participant?.event_role ||
      participant?.author_role ||
      "";
    return isHostRole(role);
  });
  const removalHostCode =
    removalEventHostCode ||
    normalizeParticipantCode(removalHostParticipant?.code || removalHostParticipant?.user?.code);
  const removalViewer = (Array.isArray(removalParticipants) ? removalParticipants : []).find(
    (participant) => participant?.is_self || participant?.is_me || participant?.me
  );
  const removalViewerCode = normalizeParticipantCode(
    removalViewer?.code || removalViewer?.user?.code
  );
  const canOpenRemoveParticipants = Boolean(
    removalEventId &&
      (isHostInEventChat ||
        removalParticipantsCanRemove ||
        chatParticipantsCanRemove)
  );
  const showRemoveParticipantsHeaderAction = Boolean(
    showParticipants && canOpenRemoveParticipants
  );
  const showParticipantReportHeaderAction = Boolean(
    showParticipants && !isSupportChat && (chatKind === "event" || chatKind === "group")
  );
  const participantReportSelectedId = getParticipantSelectionId(
    participantReportSelectedParticipant
  );
  const removeParticipantModalParticipants =
    removalParticipants.length > 0 ? removalParticipants : chatParticipantsSnapshot;

  const desktopInputBar = (
    <div className="relative w-full" style={{ "--bottom-nav-height": "0px" }}>
      <ChatInputBar {...getChatInputSlotProps()} embedded />
    </div>
  );

  useEffect(() => {
    if (isDesktop) return;
    if (showParticipants) return;
    if (!chatInputSlotOpenedRef.current) return;
    updateChatInputSlotProps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDesktop,
    showParticipants,
    showHeaderPrimaryCta,
    headerPrimaryCtaLabel,
    onHeaderPrimaryCtaClick,
    showNewJoinersHeaderCta,
    bottomPrimaryCtaLabel,
    bottomPrimaryCtaAction,
  ]);

  useEffect(() => {
    if (showParticipants || !participantReportSelecting) return;
    setParticipantReportSelecting(false);
  }, [participantReportSelecting, showParticipants]);

  const welcomeCrewPanel = chatMeta?.buddy_state?.kind === "welcome_crew" ? (
    <WelcomeCrewPanel
      state={chatMeta.buddy_state}
      chatId={internalChatId}
      onStateChange={(nextState) => {
        if (!nextState) return;
        setChatMeta((prev) => ({ ...prev, buddy_state: nextState }));
      }}
      onPickPhoto={() => fileInputRef.current?.click?.()}
    />
  ) : null;

  const viewProps = {
    inputBar: isDesktop ? desktopInputBar : null,
    viewportStyle: chatViewportStyle,

    // refs
    headerRef,
    feedRef,
    desktopModalHostRef: desktopChatModalHostRef,

    // i18n
    t,

    // header actions
    handleBackClick,
    openEventFromChatHeader,
    openClubFromChatHeader,
    headerOpensEvent: chatMeta?.buddy_state?.kind === "welcome_crew",
    openSubscriptionFromClubChat,
    headerTitle,
    isGroupChat: chatKind === "group" || isSupportChat,
    clubChat: chatMeta?.club || chat?.club || null,
    groupDisplayTitle: isSupportChat ? supportDisplayTitle : groupDisplayTitle,
    groupParticipantsLabel:
      isSupportChat
        ? t("chats.support.subtitle", { defaultValue: "Platform support" })
        : chatKind === "group"
        ? t("chats.event_chat.participants", { count: participantsCount })
        : "",
    headerSecondaryActionLabel: canManageGroupBot
      ? (
          isGroupBotToggleLoading
            ? t("chats.group_chat.bot_toggle_updating")
            : isGroupBotEnabled
              ? t("chats.group_chat.bot_toggle_on")
              : t("chats.group_chat.bot_toggle_off")
        )
      : "",
    headerSecondaryActionDisabled: !canManageGroupBot || isGroupBotToggleLoading,
    onHeaderSecondaryAction: canManageGroupBot ? handleGroupBotToggle : null,
    headerActionIconSrc:
      showParticipantReportHeaderAction
        ? "/assets/menu/alert_square.svg"
        : canRequestMessageAttention || canReportChatMessages
        ? (
            canRequestMessageAttention
              ? "/assets/menu/message_alert_white.svg"
              : "/assets/menu/alert_square.svg"
          )
        : "",
    headerActionLabel:
      showParticipantReportHeaderAction
        ? t("chats.event_chat.user_report.button_aria")
        : canRequestMessageAttention || canReportChatMessages
        ? (
            canRequestMessageAttention
              ? t("chats.event_chat.host_attention.button_aria")
              : t("chats.event_chat.message_report.button_aria")
          )
        : "",
    headerActionDisabled:
      showParticipantReportHeaderAction
        ? participantReportSubmitting
        : canRequestMessageAttention
        ? !canCreateMoreMessageAttentionRequests
        : false,
    onHeaderAction:
      showParticipantReportHeaderAction
        ? openParticipantReportModal
        : canRequestMessageAttention || canReportChatMessages
        ? (
            canRequestMessageAttention
              ? openMessageAttentionModal
              : openMessageReportModal
          )
        : null,
    headerActionActive:
      messageAttentionSelecting ||
      messageAttentionModalOpen ||
      messageReportSelecting ||
      messageReportModalOpen ||
      participantReportSelecting ||
      participantReportModalOpen,
    headerAuxActionLabel: canToggleChatNotifications
      ? (
          chatNotificationsMuted
            ? t("chats.notifications.enable_aria")
            : t("chats.notifications.disable_aria")
        )
      : "",
    headerAuxActionDisabled: chatNotificationsSubmitting,
    onHeaderAuxAction: canToggleChatNotifications ? openChatNotificationsModal : null,
    headerAuxActionActive: chatNotificationsModalOpen,
    notificationsMuted: chatNotificationsMuted,
    notificationsMutedAriaLabel: t("chats.notifications.muted_aria"),

    // event header card
    eventForCard,
    eventName,
    eventDateTime,
    eventCity,
    eventStreet,
    eventBuildingNumber,
    isFavourite,
    eventIsVerified,
    eventIsValidated,
    eventStatus,
    eventType,
    staffModerationAssessment: chatMeta?.staff_moderation_assessment || null,
    onSendHostRules: chatMeta?.staff_moderation_assessment
      ? async () => {
          if (!eventId || hostRulesReminderSending) return;
          try {
            setHostRulesReminderSending(true);
            await api.post(`/chats/${eventId}/internal-chat/host-rules-reminder/`, {});
            window.dispatchEvent(new CustomEvent("global-success", {
              detail: { message: "Hosting rules were sent to the event chat." },
            }));
          } catch (error) {
            window.dispatchEvent(new CustomEvent("global-error", {
              detail: {
                message: error?.response?.data?.error || "Could not send the hosting rules.",
                status: error?.response?.status || 400,
              },
            }));
          } finally {
            setHostRulesReminderSending(false);
          }
        }
      : null,
    sendingHostRules: hostRulesReminderSending,
    onSendCommercialNotice: chatMeta?.staff_moderation_assessment
      ? async () => {
          if (!eventId || commercialNoticeSending) return;
          try {
            setCommercialNoticeSending(true);
            const response = await api.post(
              `/chats/${eventId}/internal-chat/commercial-review-notice/`,
              {}
            );
            if (response?.data?.assessment) {
              setChatMeta((previous) => ({
                ...previous,
                staff_moderation_assessment: response.data.assessment,
              }));
            }
            requestLatestSync();
            window.dispatchEvent(new CustomEvent("global-success", {
              detail: { message: response?.data?.already_sent
                ? "This message was already sent to the event chat."
                : "Paid-event review message sent to the event chat." },
            }));
          } catch (error) {
            window.dispatchEvent(new CustomEvent("global-error", {
              detail: {
                message: error?.response?.data?.error || "Could not send the review message.",
                status: error?.response?.status || 400,
              },
            }));
          } finally {
            setCommercialNoticeSending(false);
          }
        }
      : null,
    sendingCommercialNotice: commercialNoticeSending,
    onRejectWithTemplate: chatMeta?.staff_moderation_assessment
      ? async (templateCode) => {
          if (!eventId || moderationResolutionSending) return;
          try {
            setModerationResolutionSending(templateCode);
            const response = await api.post(
              `/chats/${eventId}/internal-chat/moderation-resolution/`,
              { template_code: templateCode }
            );
            const nextAssessment = response?.data?.assessment;
            if (nextAssessment) {
              setChatMeta((previous) => ({
                ...previous,
                can_send_messages: false,
                staff_moderation_assessment: nextAssessment,
              }));
            }
            requestLatestSync();
            window.dispatchEvent(new CustomEvent("global-success", {
              detail: { message: "The event was rejected and the host was notified." },
            }));
          } catch (error) {
            window.dispatchEvent(new CustomEvent("global-error", {
              detail: {
                message: error?.response?.data?.error || "Could not reject the event.",
                status: error?.response?.status || 400,
              },
            }));
          } finally {
            setModerationResolutionSending("");
          }
        }
      : null,
    rejectingTemplateCode: moderationResolutionSending,
    showHostValidationPending: Boolean(
      isHostInEventChat &&
      eventIsValidated === false &&
      !["cancelled", "deleted", "finished", "expired_unvalidated"].includes(
        normalizedEventStatus
      )
    ),
    validationPendingIsConcept: isConceptEvent,
    onOpenHostRules: () => {
      navigate("/events/hosting-rules", {
        state: {
          mode: "review",
          returnTo: `${location.pathname}${location.search || ""}${location.hash || ""}`,
          returnState: location.state,
        },
      });
    },

    // participants capsule
    participantsCount,
    showParticipants: isSupportChat ? false : showParticipants,
    setShowParticipants: isSupportChat ? () => {} : setShowParticipants,
    participantsActionLabel: showRemoveParticipantsHeaderAction
      ? t("chats.participants.remove_button")
      : "",
    participantsActionDisabled: removalParticipantsLoading,
    onParticipantsActionClick: showRemoveParticipantsHeaderAction
      ? openRemoveParticipantsModal
      : null,
    closeCtxMenu,
    showHeaderPrimaryCta: false,
    headerPrimaryCtaLabel: "",
    onHeaderPrimaryCtaClick: null,
    showNewJoinersCta: false,
    newJoinersCtaLabel:
      String(newJoinersHeaderCta?.label || "").trim() || t("chats.groups.new_joiners_title"),
    onNewJoinersCtaClick: openNewJoinersEventFromHeader,

    // feed
    typingUsers,
    onFeedScroll,
    feedBottomPadPx,
    ctxMenu,
    imageViewer,
    items,
    pinnedMessages: getChatPinnedMessages(chatMeta),
    openCtxMenu,
    flashId,
    jumpToMessage,
    avatarByCode,
    openImageViewer,
    openProfileByCode,
    mentionContext: eventId != null ? { eventId } : null,
    currentUserId,
    allowSystemMessageActions: currentUserIsSuperuser,
    messageSelectionMode: messageAttentionSelecting || messageReportSelecting,
    selectedMessageId: messageAttentionSelecting
      ? messageAttentionSelectedMessageId
      : messageReportSelectedMessageId,
    canSelectMessage: canSelectActiveMessageItem,
    onSelectMessage: handleSelectActiveMessageItem,
    onOpenLocationMessage: handleOpenLocationMessage,
    onVotePoll: handleVotePoll,
    welcomeCrewPanel,

    // participants list
    eventId: internalChatId,
    participantsEndpoint,
    setParticipantsCount,
    onParticipantsLoaded: handleParticipantsLoaded,
    participantsReloadKey,
    participantSelectionMode: participantReportSelecting,
    selectedParticipantId: participantReportSelectedId,
    canSelectParticipant: canSelectParticipantReportTarget,
    onSelectParticipant: handleSelectParticipantReportTarget,

    // overlay
    closeImageViewer,
    copyMessage,
    beginReply,
    beginEdit,
    deleteMessage,
    onTogglePin: togglePinnedMessage,
    onToggleReaction: toggleReaction,
    canEdit,
    canDelete,
    getHeaderBottomPx,
    getBottomNavHeightPx,
    getInputBarHeightPx,
    seenPanel,
    toggleSeenPanel,
  };

  return (
    <>
      {isDesktop ? (
        <EventChatDesktopView {...viewProps} />
      ) : (
        <EventChatMobileView {...viewProps} />
      )}
      <ChatMessageAttentionModal
        visible={messageAttentionModalOpen}
        usedCount={messageAttentionRequestsUsed}
        limit={messageAttentionRequestsLimit}
        selectedPreview={messageAttentionSelectedPreview}
        selectedAuthor={messageAttentionSelectedAuthor}
        selectedMeta={messageAttentionSelectedMeta}
        desktopMountNode={desktopChatModalHostRef.current}
        canSelect={canCreateMoreMessageAttentionRequests}
        submitting={messageAttentionSubmitting}
        onClose={closeMessageAttentionModal}
        onStartSelecting={startMessageAttentionSelection}
        onConfirm={submitMessageAttentionRequest}
      />
      <ChatMessageAttentionModal
        visible={messageReportModalOpen}
        selectedPreview={messageReportSelectedPreview}
        selectedAuthor={messageReportSelectedAuthor}
        selectedMeta={messageReportSelectedMeta}
        desktopMountNode={desktopChatModalHostRef.current}
        canSelect={canReportChatMessages}
        showLimit={false}
        copyKey="message_report"
        submitting={messageReportSubmitting}
        showConfirmWhenUnselected={false}
        onClose={closeMessageReportModal}
        onStartSelecting={startMessageReportSelection}
        onConfirm={submitMessageReport}
      />
      <ChatParticipantReportModal
        visible={participantReportModalOpen}
        selectedParticipant={participantReportSelectedParticipant}
        comment={participantReportComment}
        desktopMountNode={desktopChatModalHostRef.current}
        canSelect={showParticipantReportHeaderAction}
        submitting={participantReportSubmitting}
        onCommentChange={setParticipantReportComment}
        onClose={closeParticipantReportModal}
        onStartSelecting={startParticipantReportSelection}
        onConfirm={submitParticipantReport}
      />
      <ClubChatNotificationsModal
        visible={chatNotificationsModalOpen}
        muted={chatNotificationsMuted}
        submitting={chatNotificationsSubmitting}
        desktopMountNode={desktopChatModalHostRef.current}
        onClose={closeChatNotificationsModal}
        onConfirm={submitChatNotificationsToggle}
      />
      <ChatPollSheet
        open={pollSheetOpen}
        onClose={() => setPollSheetOpen(false)}
        onSubmit={sendStructuredMessage}
        submitting={sending}
      />
      <ChatLocationSheet
        open={locationSheetOpen}
        onClose={() => setLocationSheetOpen(false)}
        onSubmit={sendStructuredMessage}
        submitting={sending}
      />
      <ConceptDatePollModal
        visible={conceptDatePollModalOpen}
        eventId={eventId}
        onClose={() => setConceptDatePollModalOpen(false)}
        onSaved={() => {}}
      />
      <ChatLocationMapModal
        open={locationMapMessageId != null}
        markers={locationMarkers}
        initialMessageId={locationMapMessageId}
        onClose={() => setLocationMapMessageId(null)}
        canDeleteMarker={(marker) => {
          const message = items.find(
            (item) => String(item?.id) === String(marker?.messageId)
          );
          return canDelete(message);
        }}
        onDeleteMarker={(marker) => {
          const message = items.find(
            (item) => String(item?.id) === String(marker?.messageId)
          );
          return deleteMessage(message);
        }}
      />
      <RemoveParticipantModal
        visible={removeParticipantModalOpen}
        eventId={removalEventId}
        participants={removeParticipantModalParticipants}
        hostCode={removalHostCode}
        viewerCode={removalViewerCode}
        onClose={() => setRemoveParticipantModalOpen(false)}
        onBack={() => setRemoveParticipantModalOpen(false)}
        onRemoved={handleRemoveParticipantsRemoved}
      />
    </>
  );
}
